import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  MessageSquarePlus,
  MessageSquare,
  Eye,
  EyeOff,
  X,
  Send,
  Check,
  Trash2,
  Smartphone,
  Monitor,
  RotateCcw,
  User as UserIcon,
  Volume2,
  VolumeX,
  ArrowUpRight,
  Camera,
  Paperclip,
  Image as ImageIcon,
  ArrowUpDown,
  Maximize2,
  Minimize2,
  Search,
  GripVertical,
  Link2,
  Mic,
  Pencil,
  Square,
  Lock,
  Tag,
} from "lucide-react";
import { useComments, parseMentions, isInPreviewIframe, ROUTE_LABELS, MAX_ATTACHMENT_BYTES, MAX_ATTACHMENTS_PER_MSG, fileToDataUrl, AUTHOR_COLORS } from "./CommentsProvider";

const IFRAME_FLAG = "__pinlayr_in_preview_iframe__";

// Where the engine's fixed UI + pins are portaled. On /demo this is
// document.body; when embedded on an external site the loader mounts the
// engine inside a Shadow DOM and sets this global so styles stay isolated.
const getPortalRoot = () =>
  (typeof window !== "undefined" && window.__PINLAYR_SHADOW_MOUNT__) || document.body;


// ── Lightweight image lightbox ───────────────────────────────────────────────
// Attachments are stored as data: URLs; opening them in a new tab just shows the
// raw base64 string. Instead we preview them in-place, Trello-style.
const openLightbox = (src) => {
  if (typeof document === "undefined") return;
  document.dispatchEvent(new CustomEvent("pinlayr:lightbox", { detail: { src } }));
};

const ImageLightbox = () => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    const onOpen = (e) => setSrc(e.detail?.src || null);
    document.addEventListener("pinlayr:lightbox", onOpen);
    return () => document.removeEventListener("pinlayr:lightbox", onOpen);
  }, []);
  useEffect(() => {
    if (!src) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setSrc(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [src]);
  if (!src) return null;
  return createPortal(
    <div
      data-comments-ui
      data-testid="image-lightbox"
      onClick={() => setSrc(null)}
      className="fixed inset-0 z-[10010] flex items-center justify-center bg-black/80 backdrop-blur-sm p-6"
    >
      <button
        type="button"
        onClick={() => setSrc(null)}
        className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
        aria-label="Close preview"
        data-testid="image-lightbox-close"
      >
        <X size={18} />
      </button>
      <img
        src={src}
        alt="attachment preview"
        onClick={(e) => e.stopPropagation()}
        className="max-w-[92vw] max-h-[88vh] object-contain rounded-lg shadow-[0_40px_120px_rgba(0,0,0,0.6)]"
      />
    </div>,
    getPortalRoot()
  );
};


const relativeTime = (iso) => {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.round((now - then) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const d = Math.floor(s / 86400);
  return d < 7 ? `${d}d ago` : new Date(iso).toLocaleDateString();
};

const initials = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");

// Render a message with @mentions highlighted in brand blue
const MessageText = ({ text, className = "" }) => {
  const parts = [];
  const re = /@([A-Za-z][A-Za-z0-9]*(?:\s[A-Za-z][A-Za-z0-9]*)?)/g;
  let last = 0;
  let m;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <span
        key={m.index}
        className="text-sunday-yellow/90 bg-sunday-yellow/10 rounded px-1 py-0.5 font-medium"
      >
        @{m[1]}
      </span>
    );
    last = re.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <p className={className}>{parts}</p>;
};

// Returns the best-contrasting text color (white or near-black) for a given
// bubble background. Uses relative luminance per WCAG so light swatches
// (Trello yellow, sky) get dark text and vivid dark ones get white.
const readableTextOn = (hex) => {
  const m = /^#?([a-f\d]{6})$/i.exec(hex || "");
  if (!m) return "#ffffff";
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  const toLin = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
  return L > 0.55 ? "#111111" : "#ffffff";
};

const Avatar = ({ author, size = 28 }) => (
  <span
    className="inline-flex items-center justify-center rounded-full font-medium shrink-0"
    style={{
      background: author.color,
      color: readableTextOn(author.color),
      width: size,
      height: size,
      fontSize: size * 0.4,
      letterSpacing: 0.4,
    }}
    aria-label={author.name}
  >
    {initials(author.name)}
  </span>
);

// ─────────────────────────────────────────────────────────────────────────────
// Element-anchored pin position
// ─────────────────────────────────────────────────────────────────────────────
// Build a reasonably stable CSS selector for an element on an arbitrary host
// page (used on external sites where there are no data-testids). Prefers a
// unique #id, otherwise a tag + :nth-of-type path up to <body>.
const buildSelector = (el) => {
  if (!el || el.nodeType !== 1) return null;
  if (el.closest && el.closest("[data-comments-ui]")) return null;
  const esc = (s) => (typeof window !== "undefined" && window.CSS && CSS.escape ? CSS.escape(s) : s);
  if (el.id && document.querySelectorAll(`#${esc(el.id)}`).length === 1) return `#${esc(el.id)}`;
  const parts = [];
  let node = el;
  while (node && node.nodeType === 1 && node !== document.body && node !== document.documentElement) {
    if (node.id && document.querySelectorAll(`#${esc(node.id)}`).length === 1) {
      parts.unshift(`#${esc(node.id)}`);
      break;
    }
    let sel = node.tagName.toLowerCase();
    const parent = node.parentElement;
    if (parent) {
      const sibs = Array.from(parent.children).filter((c) => c.tagName === node.tagName);
      if (sibs.length > 1) sel += `:nth-of-type(${sibs.indexOf(node) + 1})`;
    }
    parts.unshift(sel);
    node = node.parentElement;
    if (parts.length > 8) break;
  }
  return parts.length ? parts.join(" > ") : null;
};

const resolvePinPosition = (anchor) => {
  if (!anchor) return null;
  // Reject self-referential anchors that point at a comments-overlay element
  // (e.g. a pin's own badge). These come from older drops before the fix.
  const isSelfRef =
    anchor.testid &&
    (anchor.testid.startsWith("comment-pin-") ||
      anchor.testid.startsWith("pin-route-") ||
      anchor.testid.startsWith("pin-unread-"));
  if (anchor.testid && !isSelfRef) {
    const el = document.querySelector(`[data-testid="${anchor.testid}"]`);
    if (el && !el.closest("[data-comments-ui]")) {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        return {
          top: rect.top + window.scrollY + rect.height * anchor.relY,
          left: rect.left + window.scrollX + rect.width * anchor.relX,
          hidden: false,
        };
      }
    }
  }
  // External sites: anchor to a resolved CSS selector so pins stick to the real
  // element across scroll/reflow, using the stored relative offset within it.
  if (anchor.selector) {
    try {
      const el = document.querySelector(anchor.selector);
      if (el && !el.closest("[data-comments-ui]")) {
        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          return {
            top: rect.top + window.scrollY + rect.height * (anchor.relY || 0),
            left: rect.left + window.scrollX + rect.width * (anchor.relX || 0),
            hidden: false,
          };
        }
      }
    } catch (_e) { /* invalid selector — fall through to absolute coords */ }
  }
  if (anchor.pageX != null && anchor.pageY != null) {
    return { top: anchor.pageY, left: anchor.pageX, hidden: false };
  }
  return { top: 0, left: 0, hidden: true };
};

// Given a click event, find the nearest ancestor with a data-testid and return
// the anchor payload. On external pages (no testids) fall back to a CSS-selector
// anchor + relative offset so pins stay glued to the real element.
const buildAnchorFromClick = (evt) => {
  const target =
    document.elementFromPoint(evt.clientX, evt.clientY) || document.body;
  let el = target;
  while (el && el !== document.body && !el.dataset?.testid) el = el.parentElement;
  const viewport =
    window.innerWidth < 640
      ? "mobile"
      : window.innerWidth < 1024
      ? "tablet"
      : "desktop";
  const pageX = evt.clientX + window.scrollX;
  const pageY = evt.clientY + window.scrollY;
  const pageTitle = (typeof document !== "undefined" && document.title) || "";
  if (el && el.dataset?.testid && !el.closest?.("[data-comments-ui]")) {
    const rect = el.getBoundingClientRect();
    return {
      testid: el.dataset.testid,
      relX: (evt.clientX - rect.left) / rect.width,
      relY: (evt.clientY - rect.top) / rect.height,
      viewport,
      pageX,
      pageY,
      pageTitle,
    };
  }
  const selEl =
    target && !(target.closest && target.closest("[data-comments-ui]")) ? target : null;
  const selector = selEl ? buildSelector(selEl) : null;
  let relX = 0;
  let relY = 0;
  if (selEl && selector) {
    const rect = selEl.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      relX = (evt.clientX - rect.left) / rect.width;
      relY = (evt.clientY - rect.top) / rect.height;
    }
  }
  return { testid: null, selector, relX, relY, viewport, pageX, pageY, pageTitle };
};

// Lazily load html2canvas from a CDN the first time we need to snapshot a pin.
// Kept out of the bundle (esp. the embed bundle) so it only downloads when a
// reviewer actually drops their first pin.
let _h2cPromise = null;
const loadHtml2Canvas = () => {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.html2canvas) return Promise.resolve(window.html2canvas);
  if (_h2cPromise) return _h2cPromise;
  _h2cPromise = new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
    s.async = true;
    s.onload = () => resolve(window.html2canvas || null);
    s.onerror = () => resolve(null);
    document.head.appendChild(s);
  });
  return _h2cPromise;
};

// Snapshot a small region of the page around a pin so each ticket carries a
// thumbnail of "what the feedback is about". Returns a JPEG data URL (or null
// on failure — thumbnails are best-effort and never block comment creation).
const THUMB_W = 440;
const THUMB_H = 300;
const captureThumbnail = async (anchor) => {
  try {
    if (anchor?.pageX == null || anchor?.pageY == null) return null;
    const h2c = await loadHtml2Canvas();
    if (!h2c) return null;
    const scale = Math.min(window.devicePixelRatio || 1, 2);
    const x = Math.max(0, anchor.pageX - THUMB_W / 2);
    const y = Math.max(0, anchor.pageY - THUMB_H / 2);
    const canvas = await h2c(document.body, {
      x,
      y,
      width: THUMB_W,
      height: THUMB_H,
      scale,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
      ignoreElements: (el) =>
        el.id === "pinlayr-engine-host" ||
        (el.hasAttribute && el.hasAttribute("data-comments-ui")) ||
        (el.closest && !!el.closest("[data-comments-ui]")),
    });
    // Draw a pin marker at the click point so reviewers see exactly where.
    const ctx = canvas.getContext("2d");
    if (ctx) {
      const mx = (anchor.pageX - x) * scale;
      const my = (anchor.pageY - y) * scale;
      ctx.beginPath();
      ctx.arc(mx, my, 11 * scale, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(245,212,0,0.35)";
      ctx.fill();
      ctx.beginPath();
      ctx.arc(mx, my, 6 * scale, 0, Math.PI * 2);
      ctx.fillStyle = "#F5D400";
      ctx.fill();
      ctx.lineWidth = 2 * scale;
      ctx.strokeStyle = "#1a1a1a";
      ctx.stroke();
    }
    return canvas.toDataURL("image/jpeg", 0.62);
  } catch (_e) {
    return null;
  }
};


// Human-friendly page label for a pin/ticket. Known /demo routes use their
// mapping; external pages derive a clean name from the last path segment (or
// the captured document.title for the site root).
const prettyRoute = (comment) => {
  if (ROUTE_LABELS[comment.route]) return ROUTE_LABELS[comment.route];
  const path = (comment.route || "/").split("?")[0];
  const seg = path.replace(/\/+$/, "").split("/").filter(Boolean).pop();
  if (seg) return seg.replace(/[-_]+/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
  const t = comment.anchor?.pageTitle;
  return (t && t.split(/[|\-–—·]/)[0].trim()) || "Home";
};

// ─────────────────────────────────────────────────────────────────────────────
// Name prompt
// ─────────────────────────────────────────────────────────────────────────────
const NamePrompt = () => {
  const { namePromptOpen, closeNamePrompt, saveName, me } = useComments();
  const [name, setName] = useState(me?.name || "");
  const [color, setColor] = useState(me?.color || AUTHOR_COLORS[0]);
  useEffect(() => {
    if (namePromptOpen) {
      setName(me?.name || "");
      setColor(me?.color || AUTHOR_COLORS[0]);
    }
  }, [namePromptOpen, me]);
  if (!namePromptOpen) return null;
  const initial = (name.trim()[0] || "?").toUpperCase();
  return createPortal(
    <div
      className="fixed inset-0 z-[9998] bg-black/40 backdrop-blur-sm flex items-center justify-center px-6"
      data-testid="comments-name-prompt"
      onClick={closeNamePrompt}
    >
      <div
        className="w-full max-w-[440px] bg-white shadow-2xl border border-neutral-200 p-8 rounded-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-sunday-yellow mb-3">
          Comments
        </div>
        <h3 className="text-2xl tracking-tight font-semibold mb-2 text-neutral-900">
          {me ? "Update your name" : "What should we call you?"}
        </h3>
        <p className="text-[13px] text-neutral-600 leading-relaxed mb-6">
          Shown next to any comments or replies you leave. You can change it later.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            saveName(name, color);
          }}
        >
          {/* Live preview + name input */}
          <div className="flex items-center gap-3 mb-5">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center font-semibold text-[15px] shrink-0 shadow-sm ring-2 ring-white ring-offset-2 ring-offset-neutral-100 transition-colors"
              style={{ background: color, color: readableTextOn(color) }}
              data-testid="name-prompt-preview"
              aria-label="Bubble preview"
            >
              {initial}
            </div>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Alex"
              data-testid="name-prompt-input"
              className="flex-1 border border-neutral-300 focus:border-sunday-yellow outline-none px-4 py-3 text-[15px] rounded-md"
            />
          </div>

          {/* Color picker — Trello-style rounded swatches */}
          <div className="mb-6" data-testid="name-prompt-colors">
            <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-neutral-500 mb-2">
              Bubble color
            </div>
            <div className="flex flex-wrap gap-2">
              {AUTHOR_COLORS.map((c) => {
                const selected = c.toLowerCase() === color.toLowerCase();
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    aria-label={`Color ${c}`}
                    aria-pressed={selected}
                    data-testid={`name-prompt-color-${c.slice(1)}`}
                    className={`w-8 h-8 rounded-full transition-all outline-none hover:scale-110 ${
                      selected
                        ? "ring-2 ring-neutral-900 ring-offset-2 scale-110"
                        : "ring-1 ring-black/5"
                    }`}
                    style={{ background: c }}
                  >
                    {selected && (
                      <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 mx-auto" style={{ color: readableTextOn(c) }} aria-hidden="true">
                        <path d="M5 12l4 4L19 6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex gap-3">
            {me && (
              <button
                type="button"
                onClick={closeNamePrompt}
                className="flex-1 border border-neutral-300 py-3 text-[13px] font-medium tracking-wide hover:bg-neutral-50 rounded-md"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={!name.trim()}
              data-testid="name-prompt-save"
              className="flex-1 bg-sunday-yellow text-sunday-ink py-3 text-[13px] font-semibold tracking-[0.12em] uppercase disabled:opacity-50 rounded-md"
            >
              {me ? "Save" : "Continue"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    getPortalRoot()
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Reply Item — one message in the activity list. Owner sees inline Edit /
// Delete controls; Edit swaps the row into a compact textarea composer with
// Save / Cancel that persists via `updateReply` in CommentsProvider.
// ─────────────────────────────────────────────────────────────────────────────
const ReplyItem = ({ r, comment, me }) => {
  const { deleteReply, updateReply, role } = useComments();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(r.message);
  const [saving, setSaving] = useState(false);
  // Owner role (Sunday) is a moderator: can edit/delete anyone's reply.
  // Non-owner (client) can still only edit/delete their own replies.
  const canOwn = me?.id === r.author.id || role === "owner";

  const cancel = () => { setDraft(r.message); setEditing(false); };
  const save = async () => {
    const next = draft.trim();
    if (!next || next === r.message) { setEditing(false); return; }
    setSaving(true);
    await updateReply(comment.id, r.id, { message: next, mentions: parseMentions(next) });
    setSaving(false);
    setEditing(false);
  };

  return (
    <div
      className="bg-[#232323] border border-neutral-800 rounded-lg px-4 py-3 group"
      data-testid={`reply-${r.id}`}
    >
      <div className="flex items-center gap-2.5 mb-1.5">
        <Avatar author={r.author} size={22} />
        <div className="text-[13px] font-medium text-neutral-100">{r.author.name}</div>
        <div className="font-mono text-[9px] tracking-[0.14em] uppercase text-neutral-500">
          {relativeTime(r.createdAt)}
        </div>
        {canOwn && !editing && (
          <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
            <button
              onClick={() => { setDraft(r.message); setEditing(true); }}
              className="text-neutral-500 hover:text-neutral-100 p-1"
              aria-label="Edit reply"
              data-testid={`reply-edit-${r.id}`}
              title="Edit reply"
            >
              <Pencil size={13} />
            </button>
            <button
              onClick={() => deleteReply(comment.id, r.id, me.id)}
              className="text-neutral-500 hover:text-red-400 p-1"
              aria-label="Delete reply"
              data-testid={`reply-delete-${r.id}`}
              title="Delete reply"
            >
              <Trash2 size={13} />
            </button>
          </div>
        )}
      </div>

      {editing ? (
        <div className="pl-[30px] space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save();
              if (e.key === "Escape") cancel();
            }}
            rows={Math.min(6, Math.max(2, draft.split("\n").length))}
            autoFocus
            className="dark-scroll w-full resize-none bg-[#1A1A1A] text-neutral-100 placeholder-neutral-500 border border-neutral-700 focus:border-sunday-yellow outline-none px-3 py-2 text-[14px] leading-[1.55] rounded-md"
            data-testid={`reply-edit-input-${r.id}`}
          />
          <div className="flex items-center gap-2">
            <button
              onClick={save}
              disabled={saving || !draft.trim() || draft.trim() === r.message}
              className="bg-sunday-yellow text-sunday-ink h-8 px-3 rounded text-[12px] font-medium disabled:opacity-40 hover:bg-sunday-yellow/90 transition-colors"
              data-testid={`reply-edit-save-${r.id}`}
            >
              Save
            </button>
            <button
              onClick={cancel}
              className="text-neutral-400 hover:text-neutral-100 h-8 px-3 rounded text-[12px] font-mono tracking-[0.1em] uppercase transition-colors"
              data-testid={`reply-edit-cancel-${r.id}`}
            >
              Cancel
            </button>
            <span className="ml-auto text-[10px] font-mono tracking-[0.12em] uppercase text-neutral-500 hidden sm:inline">
              ⌘+Enter · Esc
            </span>
          </div>
        </div>
      ) : (
        <MessageText
          text={r.message}
          className="text-[14px] leading-[1.6] text-neutral-200 whitespace-pre-wrap break-words pl-[30px]"
        />
      )}

      {(r.attachments || []).length > 0 && !editing && (
        <div className="mt-2 pl-[30px] flex flex-wrap gap-2">
          {r.attachments.map((src, i) => {
            const isAudio = typeof src === "string" && src.startsWith("data:audio/");
            if (isAudio) {
              return (
                <div key={i} className="flex items-center gap-2 border border-neutral-700 rounded px-2 py-1 bg-[#141414]" data-testid={`reply-audio-${i}`}>
                  <Mic size={12} className="text-sunday-yellow" />
                  <audio src={src} controls className="h-8 max-w-[220px]" />
                </div>
              );
            }
            return (
              <button
                key={i}
                type="button"
                onClick={() => openLightbox(src)}
                className="block border border-neutral-700 rounded overflow-hidden hover:border-neutral-500 transition-colors cursor-zoom-in"
                data-testid={`reply-attachment-${i}`}
              >
                <img src={src} alt="attachment" className="max-h-20 rounded" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Label chips ("Parked" / "Internal") — small tag pills shown next to the
// ticket number in the ticket header and sidebar row. Owner sees a `+` picker
// to toggle labels; client sees chips read-only. `internal`-labeled tickets
// are never returned by the API to client viewers, so the Internal chip is
// effectively owner-only in the UI as well.
// ─────────────────────────────────────────────────────────────────────────────
const LABEL_CONFIG = {
  parked: {
    text: "Parked",
    bg: "bg-[#C89B6B]/20",
    border: "border-[#C89B6B]/40",
    color: "text-[#F2C288]",
    dot: "#C89B6B",
  },
  internal: {
    text: "Internal",
    bg: "bg-neutral-600/40",
    border: "border-neutral-500/50",
    color: "text-neutral-200",
    dot: "#9CA3AF",
  },
};

const STATUS_CHIPS = {
  in_progress: { label: "In Progress", bg: "#F97316" },
  ready_for_review: { label: "Ready for Review", bg: "#2563EB" },
};
const StatusChip = ({ status, size, testid }) => {
  const cfg = STATUS_CHIPS[status];
  if (!cfg) return null;
  const sm = size === "xs";
  return (
    <span
      className={`font-mono font-semibold uppercase rounded shrink-0 whitespace-nowrap ${sm ? "text-[9px] tracking-[0.1em] px-1.5 py-0.5" : "text-[10px] tracking-[0.12em] px-2 py-1"}`}
      style={{ background: cfg.bg, color: "#FFFFFF" }}
      data-testid={testid}
    >
      {cfg.label}
    </span>
  );
};


const LabelChip = ({ label, size = "sm", onRemove }) => {
  const cfg = LABEL_CONFIG[label];
  if (!cfg) return null;
  const sizing =
    size === "xs"
      ? "text-[9px] tracking-[0.14em] px-1.5 py-[2px]"
      : "text-[10px] tracking-[0.16em] px-2 py-0.5";
  return (
    <span
      className={`inline-flex items-center gap-1 font-mono uppercase rounded-full border ${cfg.bg} ${cfg.border} ${cfg.color} ${sizing}`}
      data-testid={`label-chip-${label}`}
    >
      {label === "internal" && <Lock size={9} strokeWidth={2.5} />}
      {cfg.text}
      {onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="ml-0.5 opacity-70 hover:opacity-100"
          aria-label={`Remove ${cfg.text} label`}
        >
          <X size={9} strokeWidth={2.5} />
        </button>
      )}
    </span>
  );
};

const LabelPicker = ({ comment, toggleLabel }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const off = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, [open]);
  const labels = comment.labels || [];
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 px-2 py-1 rounded-full border border-neutral-700 hover:border-neutral-500 text-neutral-400 hover:text-neutral-200 text-[10px] font-mono tracking-[0.12em] uppercase transition-colors"
        data-testid="label-picker-toggle"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Label ticket"
      >
        <Tag size={11} />
        Label
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 min-w-[180px] bg-[#1E1E1E] border border-neutral-700 rounded-lg shadow-[0_20px_60px_rgba(0,0,0,0.6)] z-[10001] py-1"
          role="menu"
          data-testid="label-picker-menu"
        >
          {(["parked", "internal"]).map((lbl) => {
            const on = labels.includes(lbl);
            const cfg = LABEL_CONFIG[lbl];
            return (
              <button
                key={lbl}
                type="button"
                onClick={() => { toggleLabel(comment.id, lbl); }}
                data-testid={`label-picker-${lbl}`}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-[13px] text-neutral-200 hover:bg-neutral-800 transition-colors"
                role="menuitemcheckbox"
                aria-checked={on}
              >
                <span
                  className="inline-flex items-center justify-center w-4 h-4 rounded border border-neutral-600 shrink-0"
                  style={{ background: on ? cfg.dot : "transparent" }}
                >
                  {on && <Check size={10} strokeWidth={3} color="#1E1E1E" />}
                </span>
                <span className="flex items-center gap-1.5">
                  {lbl === "internal" && <Lock size={10} className="text-neutral-400" />}
                  {cfg.text}
                </span>
                {lbl === "internal" && (
                  <span className="ml-auto font-mono text-[9px] tracking-[0.14em] uppercase text-neutral-500">
                    Owner
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Comment Card — the wide, editorial panel
// ─────────────────────────────────────────────────────────────────────────────
const CommentCard = ({ comment, onClose }) => {
  const {
    me,
    addReply,
    setStatus,
    deleteComment,
    deleteReply,
    openNamePrompt,
    knownAuthors,
    numberFor,
    updateContent,
    showSidebar,
    role,
    toggleLabel,
  } = useComments();
  const [reply, setReply] = useState("");
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [attachError, setAttachError] = useState("");
  const [titleDraft, setTitleDraft] = useState(comment.message || "");
  const [editingTitle, setEditingTitle] = useState(false);
  const [descDraft, setDescDraft] = useState(comment.description || "");
  const [editingDesc, setEditingDesc] = useState(false);
  const [descSaving, setDescSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  // Voice-note recording state
  const [recording, setRecording] = useState(false);
  const [recordSecs, setRecordSecs] = useState(0);
  const recorderRef = useRef(null);
  const recordStreamRef = useRef(null);
  const recordTimerRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const descRef = useRef(null);

  // Keep local drafts in sync when the comment updates from the server / poll
  useEffect(() => { setTitleDraft(comment.message || ""); }, [comment.message]);
  useEffect(() => { setDescDraft(comment.description || ""); }, [comment.description]);

  useEffect(() => {
    // Newest replies render at the top, so scroll to the top when the list
    // grows so the just-posted reply is immediately visible.
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [comment.replies.length]);

  // Resolve typed @names to author ids from the known-authors directory
  const resolveMentions = useCallback(
    (text) => {
      const names = parseMentions(text);
      const ids = [];
      for (const n of names) {
        const match = knownAuthors.find(
          (a) => a.name.toLowerCase() === n.toLowerCase()
        );
        if (match && !ids.includes(match.id)) ids.push(match.id);
      }
      return ids;
    },
    [knownAuthors]
  );

  const submitReply = async (e) => {
    e.preventDefault();
    if ((!reply.trim() && replyAttachments.length === 0) || sending) return;
    if (!me) {
      openNamePrompt();
      return;
    }
    setSending(true);
    await addReply(comment.id, reply, resolveMentions(reply), replyAttachments);
    setReply("");
    setReplyAttachments([]);
    setMentionQuery(null);
    setSending(false);
  };

  const acceptFiles = async (files) => {
    setAttachError("");
    const imgs = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (imgs.length === 0) {
      setAttachError("Only image files are supported.");
      return;
    }
    const remaining = MAX_ATTACHMENTS_PER_MSG - replyAttachments.length;
    const pick = imgs.slice(0, remaining);
    const encoded = [];
    for (const f of pick) {
      if (f.size > MAX_ATTACHMENT_BYTES) {
        setAttachError(`"${f.name}" exceeds 2MB.`);
        continue;
      }
      encoded.push(await fileToDataUrl(f));
    }
    if (encoded.length) setReplyAttachments((a) => [...a, ...encoded]);
  };

  // ── Voice notes ────────────────────────────────────────────────────────
  // Reviewers can record a ≤20-second voice reply via MediaRecorder. The
  // recording is stored as a base64 audio data URL in the same
  // `replyAttachments` array as image attachments — rendering checks the
  // MIME prefix and picks <audio> vs <img> accordingly.
  const VOICE_MAX_SECONDS = 20;

  const stopRecording = useCallback((discard = false) => {
    // Guard: MediaRecorder.stop fires onstop async; snapshot state now
    const rec = recorderRef.current;
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    setRecording(false);
    setRecordSecs(0);
    if (!rec) return;
    if (discard) {
      // Detach the onstop handler so the blob is dropped without saving
      rec.ondataavailable = null;
      rec.onstop = null;
    }
    try { if (rec.state !== "inactive") rec.stop(); } catch (_e) { /* ignore */ }
    recorderRef.current = null;
    // Release the mic
    const stream = recordStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      recordStreamRef.current = null;
    }
  }, []);

  const startRecording = async () => {
    setAttachError("");
    if (replyAttachments.length >= MAX_ATTACHMENTS_PER_MSG) {
      setAttachError(`Max ${MAX_ATTACHMENTS_PER_MSG} attachments per reply.`);
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setAttachError("Voice notes aren't supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordStreamRef.current = stream;
      // Pick the most widely supported audio container
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find(
        (m) => typeof MediaRecorder.isTypeSupported === "function" && MediaRecorder.isTypeSupported(m)
      ) || "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
      rec.onstop = async () => {
        try {
          const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
          if (blob.size > MAX_ATTACHMENT_BYTES) {
            setAttachError("Voice note exceeds 2MB — try a shorter clip.");
            return;
          }
          const reader = new FileReader();
          reader.onloadend = () => {
            if (typeof reader.result === "string") {
              setReplyAttachments((a) => [...a, reader.result]);
            }
          };
          reader.readAsDataURL(blob);
        } catch (_e) {
          setAttachError("Could not save voice note.");
        }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
      setRecordSecs(0);
      recordTimerRef.current = setInterval(() => {
        setRecordSecs((s) => {
          const next = s + 1;
          if (next >= VOICE_MAX_SECONDS) {
            stopRecording(false);
          }
          return next;
        });
      }, 1000);
    } catch (_e) {
      setAttachError("Microphone permission was denied.");
    }
  };

  // Release the mic and timer if the ticket unmounts mid-recording
  useEffect(() => () => stopRecording(true), [stopRecording]);

  // Detect @-typing → show autocomplete popover + auto-grow textarea up to cap
  const onReplyChange = (e) => {
    const val = e.target.value;
    setReply(val);
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 260)}px`;
    }
    const caret = e.target.selectionStart ?? val.length;
    const upToCaret = val.slice(0, caret);
    const m = /@([A-Za-z][A-Za-z0-9]*(?:\s?[A-Za-z0-9]*)?)$/.exec(upToCaret);
    if (m) {
      setMentionQuery({ start: caret - m[0].length, query: m[1] });
    } else {
      setMentionQuery(null);
    }
  };

  const acceptMention = (author) => {
    if (!mentionQuery) return;
    const before = reply.slice(0, mentionQuery.start);
    const after = reply.slice(mentionQuery.start + mentionQuery.query.length + 1);
    const next = `${before}@${author.name} ${after}`;
    setReply(next);
    setMentionQuery(null);
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const mentionMatches = useMemo(() => {
    if (!mentionQuery) return [];
    const q = mentionQuery.query.toLowerCase();
    return knownAuthors
      .filter((a) => a.name.toLowerCase().includes(q) && a.id !== me?.id)
      .slice(0, 5);
  }, [mentionQuery, knownAuthors, me]);

  const isResolved = comment.status === "resolved";
  // Owner role (Sunday) is a moderator: can delete anyone's top-level
  // comment/pin. Non-owner (client) can still only delete their own.
  const canDeleteRoot = me?.id === comment.author.id || role === "owner";

  // Title save (blur / Enter). Reverts to previous if empty.
  const commitTitle = async () => {
    setEditingTitle(false);
    const next = titleDraft.trim();
    if (!next) { setTitleDraft(comment.message || ""); return; }
    if (next === comment.message) return;
    await updateContent(comment.id, { message: next });
  };

  // Description save
  const commitDescription = async () => {
    setDescSaving(true);
    const next = descDraft.trim();
    if (next === (comment.description || "")) {
      setEditingDesc(false);
      setDescSaving(false);
      return;
    }
    await updateContent(comment.id, { description: next });
    setEditingDesc(false);
    setDescSaving(false);
  };

  // Auto-grow the description textarea when editing
  useEffect(() => {
    if (editingDesc && descRef.current) {
      descRef.current.style.height = "auto";
      descRef.current.style.height = `${Math.min(descRef.current.scrollHeight, 400)}px`;
    }
  }, [editingDesc, descDraft]);

  // ── Windowed ticket geometry: draggable + resizable ─────────────────────
  // Initial size is 70% of the FULL viewport (independent of sidebar state)
  // so opening/closing the sidebar doesn't shrink the box. The box can be
  // dragged anywhere in the viewport (including over the sidebar area).
  // Opening the sidebar resets position/size back to a centered initial
  // layout on the available (non-sidebar) area.
  const MIN_W = 750;
  const MIN_H = 400;
  const sidebarRight = showSidebar ? 420 : 0;
  const initGeom = useCallback(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Default width: same as the minimum resize width (compact).
    // Height stays at 70% of the viewport for a comfortable reading canvas.
    const availW = vw - sidebarRight; // real available for positioning
    const w = MIN_W;
    const h = Math.max(MIN_H, Math.round(vh * 0.7));
    return {
      x: Math.max(12, Math.round((availW - w) / 2)),
      y: Math.max(12, Math.round((vh - h) / 2)),
      w,
      h: Math.min(h, vh - 24),
    };
  }, [sidebarRight]);
  const [geom, setGeom] = useState(initGeom);

  // Reset position/size whenever the sidebar toggles OR the viewport resizes
  // so the box always returns to a clean centered layout on the available
  // area. Users who drag/resize get their arrangement respected until the
  // sidebar state changes.
  useEffect(() => {
    setGeom(initGeom());
    const onResize = () => setGeom(initGeom());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [initGeom]);

  // Drag from the header — allow crossing over the sidebar area
  const dragRef = useRef(null);
  const onHeaderDown = (e) => {
    if (expanded) return;
    if (e.target.closest("button, input, a")) return;
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, orig: { ...geom } };
  };
  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const { orig, startX, startY } = dragRef.current;
      // Allow the panel to overflow the left / right / bottom edges a bit
      // so the reviewer can park it against any of those sides, but always
      // keep MIN_VISIBLE (~48px) of it on-screen so there's a graspable
      // strip to drag it back. The TOP edge stays hard-clamped (y ≥ 0) so
      // the ticket header can never slip above the viewport.
      const MIN_VISIBLE = 48;
      setGeom((g) => {
        const desiredX = orig.x + (e.clientX - startX);
        const desiredY = orig.y + (e.clientY - startY);
        return {
          ...g,
          x: Math.min(Math.max(MIN_VISIBLE - g.w, desiredX), vw - MIN_VISIBLE),
          y: Math.min(Math.max(0, desiredY), vh - MIN_VISIBLE),
        };
      });
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  // Resize from the bottom-right corner — allow crossing over the sidebar
  const resizeRef = useRef(null);
  const onResizeDown = (e) => {
    if (expanded) return;
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { startX: e.clientX, startY: e.clientY, orig: { ...geom } };
  };
  useEffect(() => {
    const onMove = (e) => {
      if (!resizeRef.current) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const { orig, startX, startY } = resizeRef.current;
      setGeom((g) => {
        const w = Math.min(Math.max(MIN_W, orig.w + (e.clientX - startX)), vw - g.x);
        const h = Math.min(Math.max(MIN_H, orig.h + (e.clientY - startY)), vh - g.y);
        return { ...g, w, h };
      });
    };
    const onUp = () => { resizeRef.current = null; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const panelStyle = expanded
    ? { top: 0, left: 0, right: sidebarRight, bottom: 0, borderRadius: 0 }
    : { top: geom.y, left: geom.x, width: geom.w, height: geom.h, borderRadius: 16 };

  return (
    <>
      <div
        className={`fixed z-[9995] bg-[#1E1E1E] text-neutral-100 shadow-[0_40px_120px_rgba(0,0,0,0.55)] border border-neutral-800 flex flex-col overflow-hidden ${expanded ? "" : "ring-1 ring-white/5"}`}
        style={panelStyle}
        data-testid={`ticket-view-${comment.id}`}
        data-lenis-prevent
        onClick={(e) => e.stopPropagation()}
        onDrop={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (e.dataTransfer.files.length) await acceptFiles(e.dataTransfer.files);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
      {/* Header — meta + actions (drag handle when windowed) */}
      <div
        className={`flex items-center gap-3 px-6 py-4 border-b border-neutral-800 bg-[#171717] shrink-0 select-none ${expanded ? "" : "cursor-grab active:cursor-grabbing"}`}
        onMouseDown={onHeaderDown}
        data-testid="ticket-header"
      >
        <span
          className="font-mono text-[12px] font-semibold text-neutral-100 bg-neutral-700/80 px-2 py-1 rounded shrink-0"
          data-testid={`ticket-number-${comment.id}`}
        >
          #{numberFor(comment.id) || "?"}
        </span>
        <StatusChip status={comment.status} testid={`ticket-status-${comment.id}`} />
        {(comment.labels || []).map((lbl) => (
          <LabelChip key={lbl} label={lbl} />
        ))}
        <Avatar author={comment.author} size={30} />
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-neutral-100 truncate">
            {comment.author.name}
          </div>
          <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-500 flex items-center gap-2 mt-0.5">
            <span>{relativeTime(comment.createdAt)}</span>
            <span className="inline-flex items-center gap-1">
              {(comment.anchor?.viewport || "desktop") === "mobile" ? (
                <><Smartphone size={10} /> mobile</>
              ) : (
                <><Monitor size={10} /> desktop</>
              )}
            </span>
            {isResolved && (
              <span className="text-emerald-400">· Resolved</span>
            )}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-1">
          {role === "owner" && (
            <LabelPicker comment={comment} toggleLabel={toggleLabel} />
          )}
          <button
            onClick={() => setStatus(comment.id, isResolved ? "open" : "resolved")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-neutral-700 hover:border-emerald-400/60 hover:text-emerald-400 text-neutral-300 text-[11px] font-mono tracking-[0.12em] uppercase transition-colors"
            aria-label={isResolved ? "Reopen" : "Mark resolved"}
            data-testid="comment-resolve"
            title={isResolved ? "Reopen" : "Mark resolved"}
          >
            {isResolved ? <><RotateCcw size={12} /> Reopen</> : <><Check size={12} /> Resolve</>}
          </button>
          {canDeleteRoot && (
            <button
              onClick={() => deleteComment(comment.id, me.id)}
              className="p-2 rounded-full hover:bg-neutral-800 text-neutral-400 hover:text-red-400 transition-colors"
              aria-label="Delete comment"
              data-testid="comment-delete"
              title="Delete"
            >
              <Trash2 size={15} />
            </button>
          )}
          <button
            onClick={async () => {
              try {
                const url = new URL(window.location.href);
                url.searchParams.set("review", "1");
                url.searchParams.set("ticket", comment.id);
                const shareUrl = url.toString();
                await navigator.clipboard.writeText(shareUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 1800);
              } catch (_e) {
                // clipboard unavailable — silent
              }
            }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-mono tracking-[0.12em] uppercase transition-colors ${
              copied
                ? "border-sunday-yellow text-sunday-yellow"
                : "border-neutral-700 hover:border-sunday-yellow hover:text-sunday-yellow text-neutral-300"
            }`}
            aria-label="Copy shareable link to this ticket"
            data-testid="comment-copy-link"
            title="Copy shareable link"
          >
            {copied ? <><Check size={12} /> Copied</> : <><Link2 size={12} /> Copy link</>}
          </button>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="p-2 rounded-full hover:bg-neutral-800 text-neutral-400 hover:text-neutral-100 transition-colors"
            aria-label={expanded ? "Restore ticket size" : "Expand ticket to full screen"}
            data-testid="ticket-expand"
            title={expanded ? "Restore" : "Expand"}
          >
            {expanded ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
          </button>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-neutral-800 text-neutral-400 hover:text-neutral-100"
            aria-label="Close"
            data-testid="comment-close"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Body — scrollable */}
      <div className="dark-scroll flex-1 overflow-y-auto">
        <div className="max-w-[880px] mx-auto px-8 py-8">
          {/* Title — inline editable */}
          {editingTitle ? (
            <input
              autoFocus
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={commitTitle}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); commitTitle(); }
                if (e.key === "Escape") { setTitleDraft(comment.message || ""); setEditingTitle(false); }
              }}
              data-testid="ticket-title-input"
              className="w-full bg-transparent text-[26px] font-semibold text-neutral-100 leading-tight border-b-2 border-sunday-yellow outline-none pb-1"
            />
          ) : (
            <h1
              onClick={() => setEditingTitle(true)}
              data-testid="ticket-title"
              className="text-[26px] font-semibold text-neutral-100 leading-tight cursor-text hover:bg-neutral-800/40 rounded px-2 -mx-2 py-1 transition-colors break-words"
              title="Click to edit"
            >
              {comment.message || <span className="text-neutral-500 italic">Untitled ticket</span>}
            </h1>
          )}

          {/* Description */}
          <div className="mt-8">
            <div className="flex items-center gap-2 mb-3">
              <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-neutral-400">
                Description
              </div>
              {!editingDesc && (comment.description || "") && (
                <button
                  onClick={() => setEditingDesc(true)}
                  data-testid="ticket-desc-edit"
                  className="ml-auto text-[11px] font-mono tracking-[0.12em] uppercase text-neutral-400 hover:text-neutral-100 transition-colors"
                >
                  Edit
                </button>
              )}
            </div>
            {editingDesc ? (
              <>
                <textarea
                  ref={descRef}
                  autoFocus
                  value={descDraft}
                  onChange={(e) => setDescDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") { setDescDraft(comment.description || ""); setEditingDesc(false); }
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) commitDescription();
                  }}
                  placeholder="Add a more detailed description…"
                  data-testid="ticket-desc-input"
                  style={{ minHeight: 160, maxHeight: 400 }}
                  className="dark-scroll w-full resize-none bg-[#232323] text-neutral-100 placeholder-neutral-500 border border-neutral-700 focus:border-sunday-yellow outline-none px-4 py-3 text-[15px] leading-[1.6] rounded-lg overflow-y-auto"
                />
                <div className="flex items-center gap-2 mt-3">
                  <button
                    type="button"
                    onClick={commitDescription}
                    disabled={descSaving}
                    data-testid="ticket-desc-save"
                    className="bg-sunday-yellow text-sunday-ink px-4 py-2 text-[11px] font-semibold tracking-[0.12em] uppercase rounded-md disabled:opacity-40 hover:bg-sunday-yellow/90 transition-colors"
                  >
                    {descSaving ? "Saving…" : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setDescDraft(comment.description || ""); setEditingDesc(false); }}
                    className="px-3 py-2 text-[11px] font-medium tracking-[0.08em] uppercase text-neutral-400 hover:text-neutral-100 rounded transition-colors"
                  >
                    Cancel
                  </button>
                  <span className="ml-auto font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-500">
                    ⌘/Ctrl + Enter to save · Esc to cancel
                  </span>
                </div>
              </>
            ) : (comment.description || "").trim() ? (
              <div
                onClick={() => setEditingDesc(true)}
                data-testid="ticket-desc"
                style={{ maxHeight: 280 }}
                className="dark-scroll text-[15px] leading-[1.7] text-neutral-200 whitespace-pre-wrap break-words cursor-text hover:bg-neutral-800/40 rounded p-3 -mx-3 transition-colors overflow-y-auto"
                title="Click to edit"
              >
                {comment.description}
              </div>
            ) : (
              <button
                onClick={() => setEditingDesc(true)}
                data-testid="ticket-desc-empty"
                className="w-full text-left text-[13px] text-neutral-500 italic border border-dashed border-neutral-700 hover:border-neutral-500 hover:text-neutral-300 rounded-lg px-4 py-4 transition-colors"
              >
                + Add a more detailed description…
              </button>
            )}
          </div>

          {/* Attachments on the ticket itself */}
          {(comment.attachments || []).length > 0 && (
            <div className="mt-8">
              <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-neutral-400 mb-3">
                Attachments
              </div>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3" data-testid="root-attachments">
                {comment.attachments.map((src, i) => {
                  const isAudio = typeof src === "string" && src.startsWith("data:audio/");
                  if (isAudio) {
                    return (
                      <div key={i} className="col-span-3 md:col-span-4 border border-neutral-700 rounded-lg p-3 bg-[#141414] flex items-center gap-3">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-sunday-yellow/15 text-sunday-yellow">
                          <Mic size={13} />
                        </span>
                        <audio src={src} controls className="flex-1 h-10" data-testid={`root-audio-${i}`} />
                      </div>
                    );
                  }
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => openLightbox(src)}
                      className="block border border-neutral-700 rounded-lg overflow-hidden hover:border-neutral-500 transition-colors cursor-zoom-in"
                      data-testid={`root-attachment-${i}`}
                    >
                      <img src={src} alt="attachment" className="w-full h-28 object-cover" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Activity */}
          <div className="mt-10">
            <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-neutral-400 mb-4">
              Activity ({comment.replies.length})
            </div>

            {/* Reply composer */}
            <form
              onSubmit={submitReply}
              className="bg-[#232323] border border-neutral-700 rounded-lg p-3 relative mb-4"
              data-testid="reply-form"
            >
              {mentionMatches.length > 0 && (
                <div
                  className="dark-scroll absolute bottom-full left-3 right-3 mb-2 bg-[#2A2A2A] border border-neutral-700 rounded shadow-[0_10px_40px_rgba(0,0,0,0.5)] max-h-[180px] overflow-y-auto z-10"
                  data-testid="mention-autocomplete"
                >
                  {mentionMatches.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => acceptMention(a)}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-neutral-700/60 text-left"
                      data-testid={`mention-option-${a.id}`}
                    >
                      <Avatar author={a} size={22} />
                      <span className="text-[13px] text-neutral-100">{a.name}</span>
                    </button>
                  ))}
                </div>
              )}
              {replyAttachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2" data-testid="reply-attachments-preview">
                  {replyAttachments.map((src, i) => {
                    const isAudio = src.startsWith("data:audio/");
                    return (
                      <div key={i} className="relative">
                        {isAudio ? (
                          <audio
                            src={src}
                            controls
                            className="h-10 max-w-[220px] rounded"
                            data-testid={`reply-audio-preview-${i}`}
                          />
                        ) : (
                          <img src={src} alt="" className="w-14 h-14 object-cover rounded border border-neutral-700" />
                        )}
                        <button
                          type="button"
                          onClick={() => setReplyAttachments((a) => a.filter((_, j) => j !== i))}
                          className="absolute -top-1 -right-1 bg-red-500 text-white w-4 h-4 rounded-full flex items-center justify-center text-[9px]"
                          aria-label="Remove attachment"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {attachError && (
                <div className="text-[11px] text-red-400 mb-2" data-testid="attach-error">{attachError}</div>
              )}
              {/* Reply composer — textarea on top, action row below.
                  Two-row layout stops the tools from squeezing the input
                  when the panel is narrow. */}
              <div className="flex flex-col gap-2">
                <textarea
                  ref={textareaRef}
                  value={reply}
                  onChange={onReplyChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submitReply(e);
                    if (e.key === "Escape") setMentionQuery(null);
                  }}
                  rows={3}
                  placeholder={me ? `Reply as ${me.name}… (type @ or drop images)` : "Write a reply…"}
                  data-testid="reply-input"
                  style={{ minHeight: 84, maxHeight: 260 }}
                  className="dark-scroll w-full resize-none bg-[#1E1E1E] text-neutral-100 placeholder-neutral-500 border border-neutral-700 focus:border-sunday-yellow outline-none px-4 py-3 text-[14px] leading-[1.55] rounded-md overflow-y-auto"
                />
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 rounded text-neutral-400 hover:text-neutral-100 hover:bg-neutral-700/50"
                    aria-label="Attach image"
                    title="Attach image (or drag onto the panel) — max 2MB each"
                    data-testid="reply-attach"
                  >
                    <Paperclip size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={recording ? () => stopRecording(false) : startRecording}
                    className={`flex items-center gap-1.5 px-2.5 h-9 rounded text-[12px] font-mono tracking-[0.08em] transition-colors ${
                      recording
                        ? "bg-red-500/15 text-red-400 hover:bg-red-500/25"
                        : "text-neutral-400 hover:text-neutral-100 hover:bg-neutral-700/50"
                    }`}
                    aria-label={recording ? "Stop voice note" : "Record voice note (max 20s)"}
                    title={recording ? "Stop recording" : "Record voice note — 20s max"}
                    data-testid="reply-record"
                  >
                    {recording ? (
                      <>
                        <Square size={13} fill="currentColor" />
                        <span className="tabular-nums">
                          0:{String(recordSecs).padStart(2, "0")}
                        </span>
                        <span className="ml-1 w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                      </>
                    ) : (
                      <Mic size={16} />
                    )}
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    multiple
                    hidden
                    onChange={(e) => {
                      if (e.target.files) acceptFiles(e.target.files);
                      e.target.value = "";
                    }}
                    data-testid="reply-file-input"
                  />
                  <span className="ml-auto text-[10px] font-mono tracking-[0.12em] uppercase text-neutral-500 hidden sm:inline">
                    ⌘ + Enter to send
                  </span>
                  <button
                    type="submit"
                    disabled={(!reply.trim() && replyAttachments.length === 0) || sending}
                    className="flex items-center gap-2 bg-sunday-yellow text-sunday-ink h-9 px-4 rounded font-medium text-[13px] disabled:opacity-40 hover:bg-sunday-yellow/90 transition-colors"
                    aria-label="Send"
                    data-testid="reply-send"
                  >
                    <Send size={14} />
                    <span>Send</span>
                  </button>
                </div>
              </div>
            </form>

            {/* Reply list — bounded scroll so wheel-over-list stays contained */}
            {comment.replies.length > 0 && (
              <div
                ref={scrollRef}
                data-testid="activity-list"
                style={{ maxHeight: 360 }}
                className="dark-scroll space-y-3 overflow-y-auto pr-1"
              >
                {[...comment.replies]
                  .sort((a, b) =>
                    (b.createdAt || "").localeCompare(a.createdAt || "")
                  )
                  .map((r) => (
                    <ReplyItem
                      key={r.id}
                      r={r}
                      comment={comment}
                      me={me}
                    />
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Branded footer bar — matches the main panel surface for a seamless look */}
      <div
        className="shrink-0 flex items-center gap-3 px-5 py-1 border-t border-neutral-800 bg-[#1E1E1E] select-none"
        data-testid="powered-by"
      >
        <img
          src="https://customer-assets-agu9un31.emergentagent.net/job_anti-aging-trio-pdp-1/artifacts/qow0cij7_Pinlayr-logo.webp"
          alt="Pinlayr"
          className="h-9 w-auto"
          data-testid="pinlayr-logo"
        />
        <span className="h-5 w-px bg-neutral-700" />
        <span className="font-mono text-[9px] tracking-[0.24em] uppercase text-neutral-500">
          Powered by <span className="text-sunday-yellow">Sunday Digital</span>
        </span>
      </div>
      {/* Resize grip (bottom-right corner) — only in windowed mode */}
      {!expanded && (
        <div
          onMouseDown={onResizeDown}
          data-testid="ticket-resize"
          title="Drag to resize"
          className="absolute bottom-0 right-0 w-7 h-7 cursor-nwse-resize z-10 flex items-end justify-end p-1 group"
        >
          <svg
            width="18" height="18" viewBox="0 0 18 18" fill="none"
            className="text-sunday-yellow/70 group-hover:text-sunday-yellow transition-colors"
            aria-hidden="true"
          >
            <rect x="12" y="2" width="2" height="2" rx="1" fill="currentColor" />
            <rect x="12" y="7" width="2" height="2" rx="1" fill="currentColor" />
            <rect x="12" y="12" width="2" height="2" rx="1" fill="currentColor" />
            <rect x="7" y="7" width="2" height="2" rx="1" fill="currentColor" />
            <rect x="7" y="12" width="2" height="2" rx="1" fill="currentColor" />
            <rect x="2" y="12" width="2" height="2" rx="1" fill="currentColor" />
          </svg>
        </div>
      )}
      {/* Pinlayr branding lives in the footer bar above; resize grip stays here */}
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Pin — a numbered circle at the anchor position
//
// Positioning is applied imperatively via `translate3d` inside a
// requestAnimationFrame loop that reads the anchor's live bounding box every
// frame. This bypasses React state entirely so the pin can track sub-pixel
// scroll deltas produced by Lenis smooth-scroll without ever committing a
// re-render (which would arrive one frame late and produce visible staccato
// snapping). The button itself keeps its hover:scale-110 transform intact
// because the position transform lives on an outer wrapper.
// ─────────────────────────────────────────────────────────────────────────────
const FLIP_THRESHOLD_PX = 96; // flip the pin below its anchor when this close to the top edge

// Shared marker for live pins and the temporary placement pin. Renders a clean
// SVG teardrop (continuous white outline, tip points to the exact anchor),
// stays glued to the anchor every frame, and flips to hang downward when it
// would otherwise be clipped near the top. Optionally draggable (placement pin
// only — created pins are never movable).
const PinMarker = ({
  anchor,
  color,
  textColor,
  label,
  resolved = false,
  unread = false,
  faded = false,
  pulse = false,
  draggable = false,
  onClick,
  onDragEnd,
  zClass = "z-[9993]",
  testid,
  title,
}) => {
  const wrapperRef = useRef(null);
  const innerRef = useRef(null);
  const svgRef = useRef(null);
  const labelRef = useRef(null);
  const markerRef = useRef(null);
  const rafRef = useRef();
  const flipRef = useRef(false);
  const draggingRef = useRef(false);
  const cursorRef = useRef({ x: 0, y: 0 });

  const applyFlip = (flip) => {
    if (flip === flipRef.current) return;
    flipRef.current = flip;
    if (innerRef.current) innerRef.current.style.transform = flip ? "translate(-50%, 0)" : "translate(-50%, -100%)";
    if (svgRef.current) svgRef.current.style.transform = flip ? "scaleY(-1)" : "none";
    if (labelRef.current) labelRef.current.style.top = flip ? "65%" : "35%";
  };

  useLayoutEffect(() => {
    let alive = true;
    const tick = () => {
      if (!alive) return;
      const el = wrapperRef.current;
      if (el) {
        if (draggingRef.current) {
          const dx = cursorRef.current.x + window.scrollX;
          const dy = cursorRef.current.y + window.scrollY;
          el.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
          if (el.style.visibility !== "visible") el.style.visibility = "visible";
          applyFlip(cursorRef.current.y < FLIP_THRESHOLD_PX);
        } else {
          const next = resolvePinPosition(anchor);
          if (next && !next.hidden) {
            el.style.transform = `translate3d(${next.left}px, ${next.top}px, 0)`;
            if (el.style.visibility !== "visible") el.style.visibility = "visible";
            applyFlip(next.top - window.scrollY < FLIP_THRESHOLD_PX);
          } else if (el.style.visibility !== "hidden") {
            el.style.visibility = "hidden";
          }
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { alive = false; cancelAnimationFrame(rafRef.current); };
  }, [anchor]);

  useEffect(() => {
    if (!draggable) return;
    const onMove = (e) => {
      if (!draggingRef.current) return;
      cursorRef.current = { x: e.clientX, y: e.clientY };
    };
    const onUp = (e) => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.documentElement.style.userSelect = "";
      // Hide the pin from hit-testing so buildAnchorFromClick reads the page
      // element underneath, not the pin itself.
      const btn = markerRef.current;
      const prev = btn ? btn.style.pointerEvents : null;
      if (btn) btn.style.pointerEvents = "none";
      onDragEnd?.(e.clientX, e.clientY);
      if (btn) btn.style.pointerEvents = prev || "auto";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [draggable, onDragEnd]);

  const shadow = resolved
    ? "drop-shadow(0 4px 8px rgba(16,185,129,0.45))"
    : "drop-shadow(0 4px 7px rgba(11,37,69,0.35))";

  return (
    <div
      ref={wrapperRef}
      style={{ position: "absolute", top: 0, left: 0, visibility: "hidden", willChange: "transform", pointerEvents: "none" }}
      className={zClass}
      data-testid={testid ? `${testid}-wrapper` : undefined}
    >
      <div ref={innerRef} style={{ transform: "translate(-50%, -100%)" }} className="relative">
        <button
          ref={markerRef}
          type="button"
          onClick={(e) => { e.stopPropagation(); onClick?.(); }}
          onMouseDown={draggable ? (e) => {
            e.stopPropagation();
            e.preventDefault();
            cursorRef.current = { x: e.clientX, y: e.clientY };
            draggingRef.current = true;
            document.documentElement.style.userSelect = "none";
          } : undefined}
          style={{ pointerEvents: "auto", background: "transparent", border: "none", padding: 0, opacity: faded ? 0.6 : 1, lineHeight: 0 }}
          className={`relative block transition-transform ${draggable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer hover:scale-110"} ${pulse ? "animate-pulse" : ""}`}
          data-testid={testid}
          title={title}
        >
          <svg ref={svgRef} width="30" height="38" viewBox="0 0 34 43" style={{ filter: shadow, display: "block", transformOrigin: "center" }} aria-hidden="true">
            <path d="M17 42 C17 42 31 25 31 15 A14 14 0 1 0 3 15 C3 25 17 42 17 42 Z" fill={color} stroke="#FFFFFF" strokeWidth="2.5" />
          </svg>
          <span
            ref={labelRef}
            style={{ position: "absolute", left: "50%", top: "35%", transform: "translate(-50%, -50%)", color: textColor, pointerEvents: "none" }}
            className="font-mono font-semibold text-[10px] whitespace-nowrap"
          >
            {label != null ? label : <span className="inline-block w-1.5 h-1.5 rounded-full bg-current opacity-90" />}
          </span>
          {unread && (
            <span
              className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-red-500 border-2 border-white animate-pulse"
              aria-label="New reply"
            />
          )}
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Live pin for a created ticket. Not movable — once a ticket exists its pin is
// locked to its anchor (only the pre-create placement pin can be dragged).
const CommentPin = ({ comment, onOpen, unread }) => {
  const { numberFor } = useComments();
  const isResolved = comment.status === "resolved";
  const author = comment.author;
  const number = numberFor(comment.id);
  const pinColor = isResolved ? "#10B981" : author.color;
  const textColor = isResolved ? "#FFFFFF" : readableTextOn(author.color);
  return (
    <PinMarker
      anchor={comment.anchor}
      color={pinColor}
      textColor={textColor}
      label={number ? `#${number}` : initials(author.name)}
      resolved={isResolved}
      unread={unread && !isResolved}
      faded={(comment.labels || []).includes("parked")}
      onClick={() => onOpen(comment.id)}
      zClass="z-[9993]"
      testid={`comment-pin-${comment.id}`}
      title={`#${number || "?"} · ${author.name}: ${comment.message.slice(0, 60)}${isResolved ? " · resolved" : ""}`}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Temporary placement pin — appears the instant the user clicks a location so
// they can visually confirm (and drag to fine-tune) the exact spot BEFORE
// writing the title. Removed entirely on cancel / Escape / close / submit, so
// no empty ticket is ever left behind. Uses the current identity colour.
const DraftPin = ({ anchor, color, onReanchor }) => {
  const c = color || "#F5D400";
  return (
    <PinMarker
      anchor={anchor}
      color={c}
      textColor={readableTextOn(c)}
      label={null}
      pulse
      draggable
      onDragEnd={onReanchor}
      zClass="z-[9998]"
      testid="draft-pin"
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Add-mode composer — appears where the user clicked
// ─────────────────────────────────────────────────────────────────────────────
const DraftComposer = ({ anchor, screenPos, onCancel, onSubmit }) => {
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const { me, openNamePrompt } = useComments();

  // Open the composer BESIDE the pin (never on top of it) so the exact spot the
  // user picked stays visible while they write the title. Falls back to
  // above/below when there's no horizontal room (narrow/mobile viewports).
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  const w = Math.min(560, vw - 24);
  const composerW = Math.min(420, w);
  const estimatedHeight = 340; // matches the min textarea + header + footer

  const placeBesidePin = (cw, ch) => {
    const GAP = 30;
    const M = 12;
    const px = screenPos?.x ?? vw / 2;
    const py = screenPos?.y ?? vh / 2;
    let left = null;
    if (px + GAP + cw <= vw - M) left = Math.round(px + GAP);
    else if (px - GAP - cw >= M) left = Math.round(px - GAP - cw);
    let top;
    if (left === null) {
      left = Math.max(M, Math.min(vw - M - cw, Math.round(px - cw / 2)));
      top = py + GAP + ch <= vh - M ? Math.round(py + GAP) : Math.max(M, Math.round(py - GAP - ch));
    } else {
      top = Math.max(M, Math.min(vh - M - ch, Math.round(py - ch / 2)));
    }
    return { top, left };
  };

  const init = placeBesidePin(composerW, estimatedHeight);

  // Draggable position (once user drags the header, we take over positioning)
  const [pos, setPos] = useState({ top: init.top, left: init.left });
  const [hasBeenDragged, setHasBeenDragged] = useState(false);
  const rootRef = useRef(null);
  const dragRef = useRef(null); // {startX, startY, origTop, origLeft}

  // After mount, measure the actual composer height and re-place beside the pin.
  useLayoutEffect(() => {
    if (hasBeenDragged || !rootRef.current) return;
    const r = rootRef.current.getBoundingClientRect();
    setPos(placeBesidePin(r.width, r.height));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDragStart = (e) => {
    // Ignore drag when the user is starting inside interactive elements
    if (e.target.closest("button, a")) return;
    e.preventDefault();
    setHasBeenDragged(true);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origTop: pos.top,
      origLeft: pos.left,
    };
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setPos({
        top: Math.max(4, Math.min(vh - 80, dragRef.current.origTop + dy)),
        left: Math.max(4, Math.min(vw - 40, dragRef.current.origLeft + dx)),
      });
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [vw, vh]);

  const submit = async (e) => {
    e.preventDefault();
    if (!msg.trim() || sending) return;
    if (!me) {
      openNamePrompt();
      return;
    }
    setSending(true);
    await onSubmit(anchor, msg.trim());
  };

  return (
    <div
      ref={rootRef}
      style={{ position: "fixed", top: pos.top, left: pos.left, width: Math.min(420, w) }}
      className="z-[9996] bg-[#232323] text-neutral-100 border border-neutral-700/70 shadow-[0_30px_90px_rgba(0,0,0,0.6)] rounded-xl overflow-hidden"
      data-testid="draft-composer"
      data-lenis-prevent
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="flex items-center gap-3 px-4 py-3 border-b border-neutral-700/60 bg-[#1E1E1E] cursor-grab active:cursor-grabbing select-none"
        onMouseDown={onDragStart}
        data-testid="draft-drag-handle"
      >
        {me ? (
          <>
            <Avatar author={me} size={26} />
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-neutral-100 leading-tight truncate">
                New ticket
              </div>
              <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-500">
                as {me.name}
              </div>
            </div>
          </>
        ) : (
          <button
            onClick={openNamePrompt}
            className="flex items-center gap-2 text-[13px] text-sunday-yellow underline"
          >
            <UserIcon size={14} /> Set your name to continue
          </button>
        )}
        <button
          onClick={onCancel}
          className="ml-auto p-1.5 hover:bg-neutral-700/60 rounded-full text-neutral-400 hover:text-neutral-100 transition-colors"
          aria-label="Cancel"
          data-testid="draft-cancel"
        >
          <X size={16} />
        </button>
      </div>
      <form onSubmit={submit} className="p-4">
        <input
          autoFocus
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") onCancel();
          }}
          maxLength={140}
          placeholder="Give this ticket a short title…"
          data-testid="draft-input"
          className="w-full bg-[#1E1E1E] text-neutral-100 placeholder-neutral-500 border border-neutral-700 focus:border-sunday-yellow outline-none px-3 py-2.5 text-[14px] leading-[1.4] rounded-lg"
        />
        <div className="flex items-center justify-between mt-3">
          <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-500">
            Enter to create · Esc to cancel
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              data-testid="draft-cancel-btn"
              className="px-3 py-1.5 text-[11px] font-medium tracking-[0.08em] uppercase text-neutral-400 hover:text-neutral-100 rounded transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!msg.trim() || sending}
              data-testid="draft-submit"
              className="bg-sunday-yellow text-sunday-ink px-4 py-1.5 text-[11px] font-semibold tracking-[0.12em] uppercase rounded-md disabled:opacity-40 hover:bg-sunday-yellow/90 transition-colors shadow-[0_4px_12px_rgba(11,37,69,0.35)]"
            >
              {sending ? "Creating…" : "Create"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Sidebar list (dark theme, This-page + All-routes tabs)
// ─────────────────────────────────────────────────────────────────────────────
const Sidebar = () => {
  const {
    comments,
    allComments,
    showSidebar,
    setShowSidebar,
    setActiveId,
    hasUnread,
    mentionsMe,
    route,
    numberFor,
  } = useComments();
  const [scope, setScope] = useState("page"); // page | all
  const [filter, setFilter] = useState("open");
  const [viewportFilter, setViewportFilter] = useState("all"); // all | desktop | mobile
  const [sortOrder, setSortOrder] = useState("newest"); // newest | oldest
  const [query, setQuery] = useState("");

  const source = scope === "page" ? comments : allComments;
  const visible = useMemo(() => {
    let list = source;
    if (filter !== "all") list = list.filter((c) => (filter === "resolved" ? c.status === "resolved" : c.status !== "resolved"));
    if (viewportFilter !== "all") {
      list = list.filter(
        (c) => (c.anchor?.viewport || "desktop") === viewportFilter
      );
    }
    // Real-time text filter — searches across every visible field
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((c) => {
        const routeLabel = prettyRoute(c).toLowerCase();
        const number = String(numberFor(c.id) || "");
        const hay = [
          `#${number}`,
          c.message || "",
          c.description || "",
          c.author?.name || "",
          routeLabel,
          (c.anchor?.viewport || "desktop"),
          c.status || "",
          ...((c.replies || []).flatMap((r) => [r.message || "", r.author?.name || ""])),
        ]
          .join(" \n ")
          .toLowerCase();
        return hay.includes(q);
      });
    }
    // Sort by createdAt — sortOrder controls direction
    list = [...list].sort((a, b) => {
      const cmp = (a.createdAt || "").localeCompare(b.createdAt || "");
      return sortOrder === "newest" ? -cmp : cmp;
    });
    return list;
  }, [source, filter, viewportFilter, sortOrder, query, numberFor]);

  const openCount = source.filter((c) => c.status !== "resolved").length;
  const resolvedCount = source.filter((c) => c.status === "resolved").length;
  const desktopCount = source.filter(
    (c) => (c.anchor?.viewport || "desktop") === "desktop"
  ).length;
  const mobileCount = source.filter(
    (c) => (c.anchor?.viewport || "desktop") === "mobile"
  ).length;
  const pageUnread = comments.filter(hasUnread).length;
  const allUnread = allComments.filter(hasUnread).length;

  if (!showSidebar) return null;

  const jumpTo = (c) => {
    // If comment is on a different route, navigate first
    if (c.route !== window.location.pathname) {
      window.history.pushState({}, "", c.route);
      window.dispatchEvent(new Event("locationchange"));
      // Give the target page a moment to mount before scrolling
      setTimeout(() => {
        const pos = resolvePinPosition(c.anchor);
        if (pos && !pos.hidden) {
          window.scrollTo({
            top: Math.max(0, pos.top - window.innerHeight / 2),
            behavior: "smooth",
          });
        }
        setActiveId(c.id);
      }, 350);
      return;
    }
    const pos = resolvePinPosition(c.anchor);
    if (pos && !pos.hidden) {
      window.scrollTo({
        top: Math.max(0, pos.top - window.innerHeight / 2),
        behavior: "smooth",
      });
    }
    setActiveId(c.id);
  };

  return (
    <div
      className="fixed top-0 right-0 h-screen w-[420px] max-w-[92vw] bg-[#2A2A2A] text-neutral-100 z-[9994] shadow-[-16px_0_40px_rgba(0,0,0,0.55)] border-l border-neutral-700 flex flex-col"
      data-testid="comments-sidebar"
      data-lenis-prevent
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-700 bg-[#232323]">
        <div>
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-400">
            Comments
          </div>
          <div className="text-[15px] font-semibold text-neutral-100 mt-0.5">
            {scope === "page" ? "This page" : "All routes"}
          </div>
        </div>
        <button
          onClick={() => setShowSidebar(false)}
          className="p-1.5 hover:bg-neutral-700/60 rounded-full text-neutral-300"
          aria-label="Close comments"
          data-testid="sidebar-close"
        >
          <X size={18} />
        </button>
      </div>

      {/* Real-time search bar */}
      <div className="px-5 pt-4 pb-2 bg-[#232323]" data-testid="sidebar-search-wrap">
        <div className="relative">
          <Search
            size={13}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") setQuery(""); }}
            placeholder="Search tickets, replies, authors…"
            aria-label="Search tickets"
            data-testid="sidebar-search"
            className="w-full bg-[#1E1E1E] text-[13px] text-neutral-100 placeholder-neutral-500 border border-neutral-700 focus:border-sunday-yellow outline-none rounded-full pl-9 pr-9 py-2 transition-colors"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              data-testid="sidebar-search-clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-neutral-400 hover:text-sunday-yellow hover:bg-neutral-700/60 transition-colors"
            >
              <X size={13} />
            </button>
          )}
        </div>
        {query && (
          <div
            className="mt-2 font-mono text-[10px] tracking-[0.16em] uppercase text-neutral-500"
            data-testid="sidebar-search-count"
          >
            {visible.length} match{visible.length === 1 ? "" : "es"}
          </div>
        )}
      </div>

      {/* Scope tabs: This page vs All routes */}
      <div className="flex px-5 pt-3 gap-1 bg-[#232323]" data-testid="sidebar-scope">
        {[
          { id: "page", label: "This page", unread: pageUnread },
          { id: "all", label: "All routes", unread: allUnread },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setScope(t.id)}
            className={`relative flex items-center gap-2 px-3 py-2 text-[12px] font-medium transition-colors border-b-2 ${
              scope === t.id
                ? "text-sunday-yellow border-sunday-yellow"
                : "text-neutral-400 border-transparent hover:text-neutral-200"
            }`}
            data-testid={`sidebar-scope-${t.id}`}
          >
            {t.label}
            {t.unread > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-mono">
                {t.unread}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Viewport filter tabs — All / Desktop / Mobile */}
      <div className="flex gap-1 px-5 pt-3 pb-1 bg-[#232323]" data-testid="sidebar-viewport">
        {[
          { id: "all", label: "All", icon: null, count: source.length },
          { id: "desktop", label: "Desktop", icon: <Monitor size={11} />, count: desktopCount },
          { id: "mobile", label: "Mobile", icon: <Smartphone size={11} />, count: mobileCount },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setViewportFilter(t.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-[10px] tracking-[0.14em] uppercase border transition-colors ${
              viewportFilter === t.id
                ? "bg-white text-[#1E1E1E] border-white"
                : "text-neutral-300 border-neutral-700 hover:border-neutral-500"
            }`}
            data-testid={`sidebar-viewport-${t.id}`}
          >
            {t.icon}
            {t.label}
            <span className={`font-mono text-[10px] normal-case tracking-normal ${viewportFilter === t.id ? "text-neutral-500" : "text-neutral-500"}`}>
              {t.count}
            </span>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-1 px-5 py-3 border-b border-neutral-700 bg-[#232323]">
        {[
          { id: "open", label: `Open (${openCount})` },
          { id: "resolved", label: `Resolved (${resolvedCount})` },
          { id: "all", label: "All" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setFilter(t.id)}
            className={`px-3 py-1.5 font-mono text-[10px] tracking-[0.16em] uppercase border transition-colors rounded-full ${
              filter === t.id
                ? "bg-sunday-yellow text-sunday-ink border-sunday-yellow"
                : "text-neutral-300 border-neutral-700 hover:border-neutral-500"
            }`}
            data-testid={`sidebar-filter-${t.id}`}
          >
            {t.label}
          </button>
        ))}
        <button
          onClick={() => setSortOrder(sortOrder === "newest" ? "oldest" : "newest")}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] tracking-[0.16em] uppercase border border-neutral-700 hover:border-neutral-500 text-neutral-300 rounded-full transition-colors"
          data-testid="sidebar-sort"
          title={sortOrder === "newest" ? "Showing newest first — click for oldest" : "Showing oldest first — click for newest"}
        >
          <ArrowUpDown size={11} />
          {sortOrder === "newest" ? "Newest" : "Oldest"}
        </button>
      </div>
      <div className="dark-scroll flex-1 overflow-y-auto">
        {visible.length === 0 ? (
          <div className="px-5 py-10 text-center text-[13px] text-neutral-400">
            No comments {filter !== "all" ? `in ${filter}` : ""} yet.
            <div className="mt-2 text-neutral-500">
              Click <span className="font-medium text-neutral-300">Comment</span> below,
              then click anywhere on the page.
            </div>
          </div>
        ) : (
          visible.map((c) => {
            const unread = hasUnread(c);
            const mentioned = mentionsMe(c);
            const isOtherRoute = c.route !== route;
            return (
              <button
                key={c.id}
                onClick={() => jumpTo(c)}
                className={`w-full text-left px-5 py-4 border-b border-neutral-700/60 hover:bg-neutral-700/30 transition-colors relative ${
                  unread ? "bg-[#2f2f2f]" : ""
                }`}
                data-testid={`sidebar-item-${c.id}`}
              >
                {unread && (
                  <span
                    className={`absolute left-1.5 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full shadow-[0_0_0_3px_rgba(239,68,68,0.25)] ${
                      mentioned ? "bg-red-500 animate-pulse" : "bg-red-500"
                    }`}
                    data-testid={`sidebar-unread-${c.id}`}
                    aria-label={mentioned ? "You were mentioned" : "Unread activity"}
                  />
                )}
                <div className="flex items-center gap-2 mb-1.5">
                  <span
                    className="font-mono text-[11px] font-semibold text-neutral-300 bg-neutral-700/70 px-1.5 py-0.5 rounded"
                    data-testid={`sidebar-number-${c.id}`}
                  >
                    #{numberFor(c.id) || "?"}
                  </span>
                  <StatusChip status={c.status} size="xs" testid={`sidebar-status-${c.id}`} />
                  {(c.labels || []).map((lbl) => (
                    <LabelChip key={lbl} label={lbl} size="xs" />
                  ))}
                  <Avatar author={c.author} size={22} />
                  <span className="text-[13px] font-medium text-neutral-100">
                    {c.author.name}
                  </span>
                  {isOtherRoute && (
                    <span className="font-mono text-[9px] tracking-[0.12em] uppercase text-neutral-300 bg-neutral-700/50 rounded-full px-2 py-0.5 flex items-center gap-1">
                      <ArrowUpRight size={9} /> {c.route}
                    </span>
                  )}
                  <span className="font-mono text-[9px] tracking-[0.14em] uppercase text-neutral-400 ml-auto">
                    {relativeTime(c.createdAt)}
                  </span>
                </div>
                <p className="text-[13px] leading-relaxed text-neutral-300 line-clamp-3">
                  {c.message}
                </p>
                <div className="mt-2 font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-500 flex items-center gap-3">
                  {(c.replies.length > 0 || unread) && (
                    <span className="flex items-center gap-1.5">
                      {c.replies.length > 0 && <>{c.replies.length} {c.replies.length === 1 ? "reply" : "replies"}</>}
                      {unread && (
                        <span className="font-medium normal-case tracking-normal text-red-400">
                          {mentioned ? "· @you" : "· new"}
                        </span>
                      )}
                    </span>
                  )}
                  {c.status === "resolved" && (
                    <span className="text-emerald-400">Resolved</span>
                  )}
                  {(c.anchor?.viewport || "desktop") === "mobile" && (
                    <span className="text-neutral-500 flex items-center gap-1">
                      <Smartphone size={10} /> mobile
                    </span>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Toolbar (bottom-right floating pill)
// ─────────────────────────────────────────────────────────────────────────────
const TOOLBAR_POS_KEY = "pinlayr.comments.toolbar.pos";

const Toolbar = () => {
  const {
    mode,
    setMode,
    setActiveId,
    showSidebar,
    setShowSidebar,
    showDevicePreview,
    setShowDevicePreview,
    me,
    openNamePrompt,
    comments,
    unreadCount,
    unreadCountAllRoutes,
    mentionedCount,
    soundEnabled,
    toggleSound,
    knownAuthors,
    exitReview,
  } = useComments();
  const openCount = comments.filter((c) => c.status !== "resolved").length;

  const alertCount = Math.max(unreadCountAllRoutes, unreadCount);

  // ── Draggable toolbar ────────────────────────────────────────────────
  // Position is stored as {right, bottom} px offsets from the bottom-right
  // corner (so the toolbar stays where the user left it across window
  // resizes, matching a typical dev-tools panel). Persists in localStorage.
  const toolbarRef = useRef(null);
  const [pos, setPos] = useState(() => {
    try {
      const raw = localStorage.getItem(TOOLBAR_POS_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (Number.isFinite(p?.right) && Number.isFinite(p?.bottom)) return p;
      }
    } catch (_e) { /* ignore */ }
    return { right: 16, bottom: 16 };
  });
  const dragRef = useRef(null); // {startX, startY, startRight, startBottom}

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      const rect = toolbarRef.current?.getBoundingClientRect();
      const w = rect?.width || 0;
      const h = rect?.height || 0;
      const maxRight = Math.max(0, window.innerWidth - w - 4);
      const maxBottom = Math.max(0, window.innerHeight - h - 4);
      const nextRight = Math.min(maxRight, Math.max(4, dragRef.current.startRight - dx));
      const nextBottom = Math.min(maxBottom, Math.max(4, dragRef.current.startBottom - dy));
      setPos({ right: nextRight, bottom: nextBottom });
    };
    const onUp = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      document.body.style.userSelect = "";
      // Defer to next frame so React has committed the last setPos from
      // the trailing mousemove — reading the DOM rect before that commit
      // gives us the stale pre-drag position.
      requestAnimationFrame(() => {
        const SNAP_PX = 120;
        const INSET = 16;
        const rect = toolbarRef.current?.getBoundingClientRect();
        if (!rect) return;
        const currentLeft = rect.left;
        const currentTop = rect.top;
        const currentRight = window.innerWidth - rect.right;
        const currentBottom = window.innerHeight - rect.bottom;
        let nextRight = currentRight;
        let nextBottom = currentBottom;
        if (currentLeft < SNAP_PX) nextRight = window.innerWidth - rect.width - INSET;
        else if (currentRight < SNAP_PX) nextRight = INSET;
        if (currentTop < SNAP_PX) nextBottom = window.innerHeight - rect.height - INSET;
        else if (currentBottom < SNAP_PX) nextBottom = INSET;
        const willSnap = nextRight !== currentRight || nextBottom !== currentBottom;
        // Always persist the final resting position (snapped or not)
        try {
          localStorage.setItem(TOOLBAR_POS_KEY, JSON.stringify({ right: nextRight, bottom: nextBottom }));
        } catch (_e) { /* ignore */ }
        if (!willSnap) {
          setPos({ right: nextRight, bottom: nextBottom });
          return;
        }
        // Two-frame handoff: enable the transition first, THEN commit the
        // new position on the next frame. Without this, React batches both
        // style changes into a single paint and the browser has no old
        // transition rule to interpolate against — the pill teleports.
        setSnapping(true);
        requestAnimationFrame(() => {
          setPos({ right: nextRight, bottom: nextBottom });
          setTimeout(() => setSnapping(false), 320);
        });
      });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const [snapping, setSnapping] = useState(false);

  const startDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startRight: pos.right,
      startBottom: pos.bottom,
    };
    document.body.style.userSelect = "none";
  };

  return (
    <div
      ref={toolbarRef}
      style={{
        right: pos.right,
        bottom: pos.bottom,
        transition: snapping ? "right 240ms cubic-bezier(0.22,1,0.36,1), bottom 240ms cubic-bezier(0.22,1,0.36,1)" : "none",
      }}
      className="fixed z-[9997] flex items-center gap-1 bg-[#2A2A2A] text-neutral-100 border border-neutral-700 shadow-[0_10px_40px_rgba(0,0,0,0.5)] pl-1 pr-1 py-1 rounded-full"
      data-testid="comments-toolbar"
    >
      {/* Drag handle — grab anywhere on this grip to reposition the toolbar */}
      <button
        onMouseDown={startDrag}
        className="flex items-center justify-center w-6 h-8 rounded-full text-neutral-500 hover:text-neutral-200 hover:bg-neutral-700/70 cursor-grab active:cursor-grabbing transition-colors"
        data-testid="toolbar-drag-handle"
        title="Drag to reposition"
        aria-label="Drag toolbar"
      >
        <GripVertical size={14} />
      </button>

      {/* Exit review mode — returns to the live page */}
      <button
        onClick={exitReview}
        className="flex items-center gap-1.5 px-3 py-2 rounded-full text-[11px] font-mono tracking-[0.14em] uppercase text-neutral-300 hover:bg-neutral-700/70 transition-colors"
        data-testid="toolbar-exit-review"
        title="Exit review — back to the live page"
      >
        <X size={13} /> Exit
      </button>
      <span className="mx-0.5 h-5 w-px bg-neutral-700" aria-hidden="true" />

      {/* Add / View toggle */}
      <button
        onClick={() => {
          setActiveId(null);
          setMode(mode === "add" ? "view" : "add");
        }}
        className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-[12px] font-medium tracking-[0.06em] transition-colors ${
          mode === "add"
            ? "bg-sunday-yellow text-sunday-ink"
            : "text-neutral-200 hover:bg-neutral-700/70"
        }`}
        data-testid="toolbar-add"
        title="Add comment (click a spot on the page)"
      >
        <MessageSquarePlus size={15} />
        <span className="hidden sm:inline">Comment</span>
      </button>

      {/* Show/hide pins */}
      <button
        onClick={() => setMode(mode === "hidden" ? "view" : "hidden")}
        className={`p-2 rounded-full transition-colors ${
          mode === "hidden"
            ? "text-neutral-500"
            : "text-neutral-200 hover:bg-neutral-700/70"
        }`}
        aria-label={mode === "hidden" ? "Show pins" : "Hide pins"}
        data-testid="toolbar-visibility"
        title={mode === "hidden" ? "Show pins" : "Hide pins for demo"}
      >
        {mode === "hidden" ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>

      {/* Sidebar */}
      <button
        onClick={() => setShowSidebar(!showSidebar)}
        className={`relative p-2 rounded-full transition-colors ${
          showSidebar
            ? "bg-neutral-700 text-white"
            : "text-neutral-200 hover:bg-neutral-700/70"
        }`}
        aria-label="Toggle comments list"
        data-testid="toolbar-sidebar"
        title="All comments on this page"
      >
        <MessageSquare size={16} />
        {openCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-sunday-yellow text-sunday-ink text-[9px] font-mono w-4 h-4 flex items-center justify-center rounded-full border border-[#2A2A2A]">
            {openCount}
          </span>
        )}
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -left-1 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-[#2A2A2A] animate-pulse"
            data-testid="toolbar-unread-dot"
            aria-label={`${unreadCount} unread on this page`}
          />
        )}
      </button>

      {/* Sound toggle */}
      <button
        onClick={toggleSound}
        className={`p-2 rounded-full transition-colors ${
          soundEnabled
            ? "text-neutral-200 hover:bg-neutral-700/70"
            : "text-neutral-500 hover:bg-neutral-700/70"
        }`}
        aria-label={soundEnabled ? "Mute reply chime" : "Enable reply chime"}
        title={soundEnabled ? "Chime is on — click to mute" : "Chime is muted — click to enable"}
        data-testid="toolbar-sound"
      >
        {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
      </button>

      {/* Mention indicator (only when someone @mentioned you on another route) */}
      {mentionedCount > 0 && (
        <span
          className="flex items-center gap-1 px-2 py-1 bg-red-500/20 border border-red-500/40 rounded-full text-red-300 text-[10px] font-mono tracking-wider"
          data-testid="toolbar-mentions"
          title={`${mentionedCount} @mention${mentionedCount === 1 ? "" : "s"} for you`}
        >
          @{mentionedCount}
        </span>
      )}

      {/* Device preview (hidden inside iframe to prevent nesting) */}
      {!isInPreviewIframe && (
        <button
          onClick={() => setShowDevicePreview(!showDevicePreview)}
          className={`p-2 rounded-full transition-colors ${
            showDevicePreview
              ? "bg-neutral-700 text-white"
              : "text-neutral-200 hover:bg-neutral-700/70"
          }`}
          aria-label="Preview on mobile"
          data-testid="toolbar-device"
          title="Preview this page on a phone"
        >
          <Smartphone size={16} />
        </button>
      )}

      <div className="w-px h-6 bg-neutral-700 mx-0.5" />

      {/* Author avatar */}
      <button
        onClick={openNamePrompt}
        className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full hover:bg-neutral-700/70 transition-colors"
        data-testid="toolbar-me"
        title="Change your name"
      >
        {me ? (
          <>
            <Avatar author={me} size={26} />
            <span className="text-[12px] font-medium text-neutral-100 hidden sm:inline max-w-[100px] truncate">
              {me.name}
            </span>
          </>
        ) : (
          <>
            <span className="inline-flex items-center justify-center w-[26px] h-[26px] rounded-full bg-neutral-700 text-neutral-300">
              <UserIcon size={14} />
            </span>
            <span className="text-[12px] font-medium text-neutral-300 hidden sm:inline">
              Set name
            </span>
          </>
        )}
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Device preview modal (device toggle: iPhone 16 Pro Max & Galaxy S20 Ultra)
// ─────────────────────────────────────────────────────────────────────────────
const DEVICES = [
  { id: "iphone16pm", label: "iPhone 16 Pro Max", w: 430, h: 932, radius: 55, bezel: 14 },
  { id: "s20ultra", label: "Galaxy S20 Ultra", w: 412, h: 915, radius: 38, bezel: 12 },
];

const DevicePreview = () => {
  const { showDevicePreview, setShowDevicePreview, route, mode } = useComments();
  const [deviceId, setDeviceId] = useState("iphone16pm");
  const [fitScale, setFitScale] = useState(1);
  const [zoomMode, setZoomMode] = useState("fit"); // "fit" | 1 | 1.5 | 2
  const device = DEVICES.find((d) => d.id === deviceId) || DEVICES[0];

  const dW = device.w;
  const dH = device.h;
  const framePadding = device.bezel;

  useEffect(() => {
    if (!showDevicePreview) return;
    const compute = () => {
      const chromeVertical = 60; // small top + footer padding, switchers are absolutely positioned
      const chromeHorizontal = 40;
      const maxH = window.innerHeight - chromeVertical;
      const maxW = window.innerWidth - chromeHorizontal;
      const totalH = dH + framePadding * 2;
      const totalW = dW + framePadding * 2;
      const s = Math.min(1, maxH / totalH, maxW / totalW);
      setFitScale(s);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [showDevicePreview, dW, dH, framePadding]);

  const scale = zoomMode === "fit" ? fitScale : zoomMode;

  useEffect(() => {
    if (!showDevicePreview) return;
    const onKey = (e) => {
      if (e.key === "Escape") setShowDevicePreview(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showDevicePreview, setShowDevicePreview]);

  useEffect(() => {
    if (!showDevicePreview) return;
    const iframe = document.querySelector('[data-testid="device-iframe"]');
    if (!iframe) return;
    const send = () =>
      iframe.contentWindow?.postMessage(
        { type: "pinlayr-comments:set-mode", mode },
        "*"
      );
    send();
    iframe.addEventListener("load", send);
    return () => iframe.removeEventListener("load", send);
  }, [mode, showDevicePreview, deviceId]);

  if (!showDevicePreview) return null;

  const iframeSrc = `${route}?${IFRAME_FLAG}=1`;

  return createPortal(
    <div
      className="fixed inset-0 z-[9990] bg-[#1E1E1E]/95 backdrop-blur-md"
      data-testid="device-preview"
      data-lenis-prevent
      onClick={() => setShowDevicePreview(false)}
    >
      {/* Left rail: Back to desktop + device/zoom switchers */}
      <div
        className="absolute top-4 left-4 flex flex-col items-start gap-3 z-10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setShowDevicePreview(false)}
          data-testid="device-exit-desktop"
          className="flex items-center gap-2 bg-white text-[#1E1E1E] px-4 py-2 rounded-full text-[12px] font-medium tracking-[0.08em] uppercase hover:bg-neutral-200 transition-colors shadow-lg"
          title="Return to desktop view"
        >
          <Monitor size={15} />
          <span>Back to desktop</span>
        </button>

        {/* Device switcher */}
        <div
          className="flex flex-col bg-white/10 border border-white/20 rounded-2xl p-0.5"
          data-testid="device-switcher"
        >
          {DEVICES.map((d) => (
            <button
              key={d.id}
              onClick={() => setDeviceId(d.id)}
              className={`px-3 py-1.5 rounded-xl text-[11px] font-mono tracking-[0.14em] uppercase transition-colors text-left ${
                deviceId === d.id
                  ? "bg-white text-[#1E1E1E]"
                  : "text-white/70 hover:text-white"
              }`}
              data-testid={`device-${d.id}`}
            >
              {d.label}
            </button>
          ))}
        </div>

        {/* Zoom switcher */}
        <div
          className="flex flex-col bg-white/10 border border-white/20 rounded-2xl p-0.5"
          data-testid="device-zoom-switcher"
        >
          {[
            { id: "fit", label: "Fit" },
            { id: 1, label: "100%" },
            { id: 1.5, label: "150%" },
          ].map((z) => (
            <button
              key={String(z.id)}
              onClick={() => setZoomMode(z.id)}
              className={`px-3 py-1.5 rounded-xl text-[11px] font-mono tracking-[0.14em] uppercase transition-colors text-left ${
                zoomMode === z.id
                  ? "bg-white text-[#1E1E1E]"
                  : "text-white/70 hover:text-white"
              }`}
              data-testid={`device-zoom-${z.id}`}
              title={
                z.id === "fit"
                  ? "Scale phone to fit window"
                  : `View phone at ${z.label} pixel size`
              }
            >
              {z.label}
            </button>
          ))}
        </div>
      </div>

      {/* Right rail: screenshot + close */}
      <div
        className="absolute top-4 right-4 flex items-center gap-2 z-10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={async () => {
            // Capture the current iframe content as PNG via html2canvas (loaded on demand)
            const ensureLib = () =>
              new Promise((resolve, reject) => {
                if (window.html2canvas) return resolve(window.html2canvas);
                const s = document.createElement("script");
                s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
                s.onload = () => resolve(window.html2canvas);
                s.onerror = () => reject(new Error("html2canvas load failed"));
                document.head.appendChild(s);
              });
            try {
              const html2canvas = await ensureLib();
              const iframe = document.querySelector('[data-testid="device-iframe"]');
              if (!iframe) return;
              const doc = iframe.contentDocument;
              if (!doc) return;
              const canvas = await html2canvas(doc.body, {
                useCORS: true,
                backgroundColor: "#ffffff",
                width: dW,
                height: dH,
                windowWidth: dW,
                windowHeight: dH,
                scale: 2,
              });
              const link = document.createElement("a");
              link.download = `pinlayr-${device.id}-${Date.now()}.png`;
              link.href = canvas.toDataURL("image/png");
              link.click();
            } catch (err) {
              alert("Screenshot capture failed: " + err.message);
            }
          }}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
          aria-label="Download screenshot"
          title="Download PNG of current phone view"
          data-testid="device-screenshot"
        >
          <Camera size={16} />
        </button>
        <button
          onClick={() => setShowDevicePreview(false)}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
          aria-label="Close mobile preview"
          data-testid="device-close"
        >
          <X size={16} />
        </button>
      </div>

      <div className="absolute inset-0 flex items-start justify-center overflow-auto pt-6 pb-8">
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: (dW + framePadding * 2) * scale,
            height: (dH + framePadding * 2) * scale,
            flexShrink: 0,
          }}
          className="relative"
        >
          <div
            style={{
              width: dW + framePadding * 2,
              height: dH + framePadding * 2,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              borderRadius: `${device.radius}px`,
              padding: framePadding,
            }}
            className="relative bg-black shadow-[0_40px_80px_rgba(0,0,0,0.55)]"
          >
            <iframe
              title="Mobile preview"
              key={deviceId}
              src={iframeSrc}
              style={{
                width: dW,
                height: dH,
                border: 0,
                borderRadius: `${Math.max(0, device.radius - framePadding)}px`,
              }}
              className="bg-white overflow-hidden block"
              data-testid="device-iframe"
            />
          </div>
        </div>
      </div>

      <div
        className="absolute bottom-2 left-1/2 -translate-x-1/2 font-mono text-[10px] tracking-[0.24em] uppercase text-white/40 text-center whitespace-nowrap pointer-events-none"
      >
        {device.label} · {dW}×{dH} · zoom {zoomMode === "fit" ? `fit (${Math.round(fitScale * 100)}%)` : `${Math.round(scale * 100)}%`} · click <span className="text-white/70">Comment</span> then tap inside the phone
      </div>
    </div>,
    getPortalRoot()
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Review Disclaimer — one-time modal shown the first time a reviewer enters
// review mode. Explains that this view intentionally un-sticks sticky
// sections, freezes animations, and disables smooth-scroll so pins have a
// stable layout to anchor against. Dismissed permanently via localStorage.
// ─────────────────────────────────────────────────────────────────────────────
const REVIEW_DISCLAIMER_KEY = "pinlayr.review.disclaimer.seen";

const ReviewDisclaimer = () => {
  const { reviewMode } = useComments();
  const [open, setOpen] = useState(false);
  const isEmbed = typeof window !== "undefined" && !!window.__PINLAYR_EMBED__;

  useEffect(() => {
    if (!reviewMode) return;
    let seen = false;
    try {
      seen = localStorage.getItem(REVIEW_DISCLAIMER_KEY) === "1";
    } catch (_e) { /* ignore */ }
    if (!seen) setOpen(true);
  }, [reviewMode]);

  const dismiss = () => {
    try {
      localStorage.setItem(REVIEW_DISCLAIMER_KEY, "1");
    } catch (_e) { /* ignore */ }
    setOpen(false);
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
      data-comments-ui
      data-testid="review-disclaimer"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={dismiss}
        aria-hidden="true"
      />
      {/* Card */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-disclaimer-title"
        className="relative w-full max-w-[460px] bg-[#1E1E1E] border border-neutral-700 rounded-xl shadow-[0_40px_80px_rgba(0,0,0,0.6)] overflow-hidden"
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-neutral-800">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-sunday-yellow text-sunday-ink">
            <Eye size={14} strokeWidth={2.5} />
          </span>
          <div>
            <h2
              id="review-disclaimer-title"
              className="text-[15px] font-medium text-neutral-100 leading-tight"
            >
              {isEmbed ? "You're in Review mode" : "You're in Preview & Review mode"}
            </h2>
            <p className="text-[11px] font-mono tracking-[0.16em] uppercase text-sunday-yellow mt-0.5">
              {isEmbed ? "Click to leave feedback" : "Read before you comment"}
            </p>
          </div>
        </div>
        <div className="px-5 py-4 text-[13px] leading-[1.6] text-neutral-300 space-y-3">
          {isEmbed ? (
            <>
              <p>
                The page stays fully live and interactive while you review —
                nothing is frozen. Just point and click to leave feedback:
              </p>
              <ul className="space-y-2 text-neutral-300">
                <li className="flex gap-2">
                  <span className="text-sunday-yellow mt-[1px]">–</span>
                  <span><span className="text-neutral-100 font-medium">Click anywhere</span> on the page to drop a pin and start a comment.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-sunday-yellow mt-[1px]">–</span>
                  <span><span className="text-neutral-100 font-medium">Pins stay anchored</span> to the element you clicked, even as the page scrolls.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-sunday-yellow mt-[1px]">–</span>
                  <span><span className="text-neutral-100 font-medium">Keep browsing normally</span> — links, buttons and animations all keep working.</span>
                </li>
              </ul>
              <p className="text-neutral-400 text-[12px] pt-1">
                Exit review mode any time from the toolbar to hide the pins.
              </p>
            </>
          ) : (
            <>
              <p>
                This is a static preview built for feedback. So pins stay
                glued to the right elements, a few live-page behaviors are
                intentionally paused:
              </p>
              <ul className="space-y-2 text-neutral-300">
                <li className="flex gap-2">
                  <span className="text-sunday-yellow mt-[1px]">–</span>
                  <span><span className="text-neutral-100 font-medium">Sticky bars & floating elements</span> flow inline instead of following the scroll.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-sunday-yellow mt-[1px]">–</span>
                  <span><span className="text-neutral-100 font-medium">Smooth-scroll & scroll-triggered animations</span> are turned off — the page uses native browser scroll.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-sunday-yellow mt-[1px]">–</span>
                  <span><span className="text-neutral-100 font-medium">Some hover/reveal micro-interactions</span> may freeze in place.</span>
                </li>
              </ul>
              <p className="text-neutral-400 text-[12px] pt-1">
                Exit review mode any time from the toolbar to see the page as it renders live.
              </p>
            </>
          )}
        </div>
        <div className="px-5 py-4 border-t border-neutral-800 flex justify-end">
          <button
            onClick={dismiss}
            className="bg-sunday-yellow text-sunday-ink px-4 py-2 rounded font-medium text-[13px] hover:bg-sunday-yellow/90 transition-colors"
            data-testid="review-disclaimer-dismiss"
            autoFocus
          >
            Got it — let&apos;s review
          </button>
        </div>
      </div>
    </div>,
    getPortalRoot()
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Review Transition Loader — full-screen fade shown during the reload
// handshake when the reviewer enters or exits review mode. The reload
// gives us a clean cold-start into (or out of) review mode with zero
// chance of Lenis / sticky-freeze / animation-pause race conditions.
// ─────────────────────────────────────────────────────────────────────────────
const ReviewTransitionLoader = () => {
  const { transitioning, reviewMode } = useComments();
  if (!transitioning) return null;
  const label = reviewMode ? "Returning to live view…" : "Preparing review view…";
  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-[#1E1E1E]/95 backdrop-blur-sm"
      data-comments-ui
      data-testid="review-transition-loader"
      role="status"
      aria-live="polite"
    >
      <div className="flex flex-col items-center gap-4">
        <div className="relative w-12 h-12">
          <div className="absolute inset-0 rounded-full border-2 border-neutral-700" />
          <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-sunday-yellow animate-spin" />
        </div>
        <div className="text-neutral-100 text-[13px] font-medium tracking-[0.02em]">{label}</div>
        <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-sunday-yellow">Pinlayr</div>
      </div>
    </div>,
    getPortalRoot()
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Review CTA — compact yellow bubble bottom-right that expands on hover into
// the full "Review this page" pill. Auto-lifts when the sticky Add-to-Cart
// bar appears so the two never overlap. First visit plays a one-shot bounce
// hint (stored in localStorage). Tapping opens Review Mode directly.
// ─────────────────────────────────────────────────────────────────────────────
const ReviewCTA = () => {
  const { enterReview, comments, unreadCountAllRoutes } = useComments();
  const openCount = comments.filter((c) => c.status !== "resolved").length;

  // Per-project embed appearance (label / colour / position), if configured.
  const cfg = (typeof window !== "undefined" && window.__PINLAYR_EMBED__) || {};
  const ctaLabel = cfg.widgetLabel || "Review this page";
  const sideClass = cfg.widgetPosition === "bottom-left" ? "left-5" : "right-5";
  const customColor = cfg.widgetColor || null;
  const inkFor = (hex) => {
    try {
      const h = hex.replace("#", "");
      const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
      return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? "#111111" : "#ffffff";
    } catch (_e) { return "#111111"; }
  };
  const ctaStyle = customColor
    ? { willChange: "transform, bottom", background: customColor, color: inkFor(customColor) }
    : { willChange: "transform, bottom" };

  const [expanded, setExpanded] = useState(false);
  const [lifted, setLifted] = useState(false);
  const [bounce, setBounce] = useState(false);

  // Auto-lift above the PDP sticky cart bar whenever it's mounted.
  useEffect(() => {
    const el = () => document.querySelector('[data-testid="sticky-cart-bar"]');
    const sync = () => setLifted(!!el());
    sync();
    const mo = new MutationObserver(sync);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, []);

  // First-visit gentle bounce hint (2s delay after mount, once per browser).
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (localStorage.getItem("reviewCtaSeen") === "1") return;
    } catch (_) { return; }
    const t = setTimeout(() => {
      setBounce(true);
      setTimeout(() => setBounce(false), 900);
      try { localStorage.setItem("reviewCtaSeen", "1"); } catch (_) {}
    }, 2000);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <ReviewTransitionLoader />
      {createPortal(
        <div data-comments-ui>
          <button
            onClick={enterReview}
            onMouseEnter={() => setExpanded(true)}
            onMouseLeave={() => setExpanded(false)}
            onFocus={() => setExpanded(true)}
            onBlur={() => setExpanded(false)}
            data-testid="review-cta"
            data-expanded={expanded}
            className={`fixed ${sideClass} z-[9997] group flex items-center rounded-full shadow-[0_16px_48px_rgba(0,0,0,0.45)] ${customColor ? "" : "bg-sunday-yellow text-sunday-ink hover:bg-sunday-yellow"} transition-[bottom,transform,padding] duration-[240ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
              lifted ? "bottom-[88px] lg:bottom-[96px]" : "bottom-5"
            } ${bounce ? "review-cta-bounce" : ""} ${expanded ? "pl-3 pr-4 py-2" : "p-2"}`}
            style={ctaStyle}
            title="Enter review mode — leave comments on this page"
            aria-label={ctaLabel}
          >
            <span
              className={`inline-flex items-center justify-center w-7 h-7 rounded-full shrink-0 ${customColor ? "" : "text-sunday-ink"}`}
              aria-hidden="true"
            >
              <MessageSquarePlus size={15} strokeWidth={2.5} />
            </span>
            {/* Expanding label — collapses to width 0 when not hovered */}
            <span
              className={`overflow-hidden whitespace-nowrap flex items-center gap-2 transition-[max-width,margin,opacity] duration-[240ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
                expanded ? "max-w-[240px] ml-2 opacity-100" : "max-w-0 ml-0 opacity-0"
              }`}
            >
              <span className="text-[13px] font-semibold tracking-[0.02em]">{ctaLabel}</span>
              {openCount > 0 && (
                <span
                  className="min-w-[22px] h-[22px] px-1.5 flex items-center justify-center font-mono text-[10px] font-semibold rounded-full bg-sunday-ink/15 text-sunday-ink"
                  data-testid="review-cta-count"
                >
                  {openCount}
                </span>
              )}
            </span>
            {unreadCountAllRoutes > 0 && (
              <span
                className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500 border-2 border-sunday-yellow animate-pulse"
                data-testid="review-cta-unread"
                aria-label={`${unreadCountAllRoutes} unread replies`}
              />
            )}
          </button>
    </div>,
    getPortalRoot()
  )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main overlay — pins, active card, add-mode capture, toolbar, sidebar
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
export const CommentsOverlay = () => {
  const {
    comments,
    mode,
    setMode,
    activeId,
    setActiveId,
    addComment,
    me,
    openNamePrompt,
    knownAuthors,
    namePromptOpen,
    hasUnread,
    markSeen,
    viewport,
    showDevicePreview,
    reviewMode,
  } = useComments();
  const [draft, setDraft] = useState(null); // {anchor, screenPos}
  const [pendingClick, setPendingClick] = useState(null);

  // Filter pins by viewport — mobile pins only on mobile, desktop pins on desktop
  const viewportComments = useMemo(
    () => comments.filter((c) => (c.anchor?.viewport || "desktop") === viewport),
    [comments, viewport]
  );

  // Mark comment as seen whenever the user opens it
  useEffect(() => {
    if (activeId) markSeen(activeId);
  }, [activeId, markSeen]);

  useEffect(() => {
    if (me && pendingClick) {
      setDraft(pendingClick);
      setPendingClick(null);
    }
  }, [me, pendingClick]);

  // Re-anchor the placement pin after the user drags it (before ticket creation).
  const handleDraftReanchor = useCallback((clientX, clientY) => {
    const newAnchor = buildAnchorFromClick({ clientX, clientY });
    setDraft((d) => (d ? { ...d, anchor: newAnchor } : d));
  }, []);

  // When we're inside the mobile-preview iframe, listen for mode changes from
  // the parent and forward captured clicks back to it.
  useEffect(() => {
    if (!isInPreviewIframe) return;
    const onMessage = (evt) => {
      if (evt?.data?.type === "pinlayr-comments:set-mode") {
        setMode(evt.data.mode);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [setMode]);

  // Capture clicks in Add mode
  useEffect(() => {
    if (mode !== "add") return;
    document.documentElement.classList.add("comments-add-mode");
    const onClick = (e) => {
      // Detect clicks on Pinlayr's own UI. composedPath() is REQUIRED so this
      // pierces the embed Shadow DOM — otherwise e.target is retargeted to the
      // shadow host and closest() sees none of our attributes, causing the
      // handler to cancel the click before the modal's React handlers run.
      const path = (typeof e.composedPath === "function" && e.composedPath()) || [];
      const inCommentsUi =
        path.some(
          (n) =>
            n instanceof Element &&
            (n.id === "pinlayr-engine-host" ||
              n.hasAttribute?.("data-comments-ui") ||
              n.matches?.(
                "[data-comments-ui],[data-testid^='comment-'],[data-testid='comments-toolbar'],[data-testid='comments-sidebar'],[data-testid='draft-composer'],[data-testid='comments-name-prompt'],[data-testid='draft-pin'],[data-testid='draft-pin-wrapper']"
              ))
        ) ||
        e.target.closest?.("[data-comments-ui]") ||
        e.target.closest?.("[data-testid^='comment-']") ||
        e.target.closest?.("[data-testid='comments-toolbar']") ||
        e.target.closest?.("[data-testid='comments-sidebar']") ||
        e.target.closest?.("[data-testid='draft-composer']") ||
        e.target.closest?.("[data-testid='draft-pin']") ||
        e.target.closest?.("[data-testid='draft-pin-wrapper']") ||
        e.target.closest?.("[data-testid='comments-name-prompt']");
      if (inCommentsUi) return;
      e.preventDefault();
      e.stopPropagation();
      const anchor = buildAnchorFromClick(e);

      // If we're inside the iframe, forward the click UP to the parent so
      // the composer opens in the desktop chrome — never inside the phone.
      if (isInPreviewIframe) {
        // Force viewport to mobile since we're in the phone frame
        anchor.viewport = "mobile";
        window.parent?.postMessage(
          {
            type: "pinlayr-comments:add-click",
            anchor,
            // pass screenPos in iframe-local coords; parent maps to its own screen
          },
          "*"
        );
        return;
      }

      const screenPos = { x: e.clientX, y: e.clientY };
      if (!me) {
        setPendingClick({ anchor, screenPos });
        openNamePrompt();
        return;
      }
      setDraft({ anchor, screenPos });
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMode("view");
        setDraft(null);
      }
    };
    document.addEventListener("click", onClick, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("keydown", onKey);
      document.documentElement.classList.remove("comments-add-mode");
    };
  }, [mode, me, openNamePrompt, setMode]);

  // Parent-side: listen for add-clicks originating in the iframe, open the composer here
  useEffect(() => {
    if (isInPreviewIframe) return;
    const onMessage = (evt) => {
      if (evt?.data?.type === "pinlayr-comments:add-click") {
        const anchor = evt.data.anchor;
        // Position composer at the center of the visible mobile-preview iframe
        const iframe = document.querySelector('[data-testid="device-iframe"]');
        const rect = iframe?.getBoundingClientRect();
        const screenPos = rect
          ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
          : { x: window.innerWidth / 2, y: window.innerHeight / 2 };
        if (!me) {
          setPendingClick({ anchor, screenPos });
          openNamePrompt();
        } else {
          setDraft({ anchor, screenPos });
        }
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [me, openNamePrompt]);

  // Position the active card centered on the viewport by default
  // (TicketView is a fixed full-panel now; positioning is not needed.)

  const active = comments.find((c) => c.id === activeId);
  const visibleComments = mode === "hidden" ? [] : viewportComments;

  const submitDraft = async (anchor, message) => {
    // Resolve any @names to stable ids using the known-authors directory
    // (which now includes workspace members + reviewers, not just past authors).
    const names = parseMentions(message);
    const ids = [];
    for (const a of knownAuthors) {
      if (names.some((n) => n.toLowerCase() === a.name.toLowerCase()) && !ids.includes(a.id)) {
        ids.push(a.id);
      }
    }
    await addComment(anchor, message, ids, [], await captureThumbnail(anchor));
    setDraft(null);
    setMode("view");
  };

  const pinsLayer = (
    <div className="pointer-events-none absolute inset-0" data-comments-ui>
      {visibleComments.map((c, i) => (
        <span key={c.id} className="pointer-events-auto">
          <CommentPin
            comment={c}
            unread={hasUnread(c)}
            onOpen={(id) => setActiveId(id)}
          />
        </span>
      ))}
    </div>
  );

  // Comment-placement mode: show a crosshair on the whole page so reviewers
  // know exactly where they're about to drop a pin.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const rootEl = document.documentElement;
    if (mode === "add") {
      const prev = rootEl.style.cursor;
      rootEl.style.cursor = "crosshair";
      return () => { rootEl.style.cursor = prev; };
    }
    rootEl.style.cursor = "";
    return undefined;
  }, [mode]);

  // Live-page mode: reviewers see only the "Review this page" CTA. Pins,
  // toolbar, sidebar, and add-mode are all suppressed until the CTA is
  // clicked (which flips reviewMode → true via CommentsProvider).
  // Inside the mobile-preview iframe we bypass this gate because the
  // parent's review mode is what controls the iframe's UI.
  if (!reviewMode && !isInPreviewIframe) {
    // In embed mode, only offer the launcher to someone who arrived with an
    // identity token — team via the dashboard "Review", or a client via their
    // share link. Anonymous walk-ups (no token) get no launcher and no prompt.
    const embedCfg = (typeof window !== "undefined" && window.__PINLAYR_EMBED__) || null;
    if (embedCfg && !embedCfg.reviewerToken) return null;
    return <ReviewCTA />;
  }

  return (
    <>
      {/* Pins live in a full-document layer (relative to <body>).
          When the mobile-preview modal is open in the parent, we hide these
          desktop pins so they don't leak on top of the modal — the iframe has
          its own overlay that renders the correct mobile pins inside the phone. */}
      {!(showDevicePreview && !isInPreviewIframe) &&
        createPortal(
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: document.documentElement.scrollHeight,
            pointerEvents: "none",
          }}
        >
          {pinsLayer}
          {draft && <DraftPin anchor={draft.anchor} color={me?.color} onReanchor={handleDraftReanchor} />}
        </div>,
        getPortalRoot()
      )}

      {/* Everything below is ONLY rendered in the desktop parent — inside the
          mobile-preview iframe we intentionally keep only pins + pointing. */}
      {!isInPreviewIframe && (
        <>
          {active &&
            mode !== "hidden" &&
            createPortal(
              <div data-comments-ui>
                <CommentCard
                  comment={active}
                  onClose={() => setActiveId(null)}
                />
              </div>,
              getPortalRoot()
            )}

          {draft &&
            createPortal(
              <div data-comments-ui>
                <DraftComposer
                  anchor={draft.anchor}
                  screenPos={draft.screenPos}
                  onCancel={() => {
                    setDraft(null);
                    setMode("view");
                  }}
                  onSubmit={submitDraft}
                />
              </div>,
              getPortalRoot()
            )}

          <div data-comments-ui>
            <ImageLightbox />
            <Toolbar />
            <Sidebar />
            <DevicePreview />
            <ReviewDisclaimer />
            <ReviewTransitionLoader />
            {mode === "add" && !namePromptOpen && !draft && (
              <div
                className="fixed top-4 left-1/2 -translate-x-1/2 z-[9997] bg-sunday-yellow text-sunday-ink px-5 py-2.5 font-mono text-[11px] tracking-[0.18em] uppercase shadow-[0_10px_30px_rgba(11,37,69,0.35)] rounded-full flex items-center gap-3"
                data-testid="add-mode-banner"
              >
                <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                Click anywhere to leave a comment
                <button
                  onClick={() => setMode("view")}
                  className="ml-2 text-white/70 hover:text-white text-[10px] underline"
                >
                  Cancel · Esc
                </button>
              </div>
            )}
          </div>
          <NamePrompt />
        </>
      )}
    </>
  );
};

export default CommentsOverlay;
